/**
 * Engraving Stepper - JavaScript Controller
 * Handles all 5 steps of the custom engraving wizard
 */

(function() {
  'use strict';

  // Find all stepper sections on the page
  document.querySelectorAll('.engrave-stepper').forEach(initStepper);

  function initStepper(root) {
    const sectionId = root.dataset.sectionId;
    const configEl = document.getElementById(`stepper-config-${sectionId}`);
    if (!configEl) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('Failed to parse stepper config:', e);
      return;
    }

    // ========== Device Pixel Ratio for Sharp Previews ==========
    // Force minimum 2x resolution for sharper canvas previews on all displays
    const MIN_QUALITY_SCALE = 2;
    const DPR = Math.max(window.devicePixelRatio || 1, MIN_QUALITY_SCALE);
    const BASE_SIZE = 472; // Base canvas size in CSS pixels (40mm at 300 DPI)
    const CANVAS_SIZE = Math.round(BASE_SIZE * DPR); // Actual canvas pixel size (944px minimum)

    // ========== State ==========
    const state = {
      currentStep: 1,
      completedSteps: new Set(),
      originalImage: null,
      originalFileName: 'design',
      processedImageData: null,
      threshold: 165,
      contrast: 1.3,
      fillGaps: true,  // Morphological closing to fill small gaps in color selection
      gapFillSize: 50,        // Max gap size to fill (0-200 px)
      inverted: false,
      // Preprocessing controls
      blurRadius: 0,          // Gaussian blur radius (0-5, step 0.5)
      brightness: 0,          // Brightness shift (-100 to +100)
      antiAliasing: true,     // Edge smoothing toggle
      despeckleSize: 0,       // Remove small black spots (0-200 px)
      thresholdMode: 'global', // 'global' or 'adaptive' (Sauvola)
      sauvolaWindowSize: 25,   // Adaptive threshold window (11-51)
      sauvolaK: 0.3,           // Adaptive threshold sensitivity (0.1-0.5)
      // Vectorization controls
      turdsize: 4,            // Speckle filter (0-20)
      cornerSharpness: 1.0,   // alphamax (0-1.34)
      curveOptimization: 0.2, // opttolerance (0-2)
      turnPolicy: 'minority', // turnpolicy
      minDetailSize: 0,       // SVG post-processing: remove paths with area < this (0 = disabled)
      // Upscaled preprocessing cache
      upscaledCanvas: null,   // Cached upscaled source canvas
      upscaleInfo: null,      // {scale, width, height}
      // Vectorized preview
      previewSvg: null,       // Cached SVG string from debounced vectorization
      // Color selection mode
      colorModeEnabled: false,
      colorModeSlidersReset: false, // Track if sliders were reset for color mode
      detectedColors: [],      // Array of {r, g, b, hex, count, selected}
      colorCount: 8,           // Number of colors to detect
      textLines: {
        top: { enabled: false, text: '', font: 'Roboto', bold: false, italic: false, underline: false, size: '22', x: 0.2, y: 0.82 },
        middle: { enabled: false, text: '', font: 'Roboto', bold: false, italic: false, underline: false, size: '22', x: 0.2, y: 0.88 },
        bottom: { enabled: false, text: '', font: 'Roboto', bold: false, italic: false, underline: false, size: '22', x: 0.2, y: 0.94 }
      },
      // Image transform for logo positioning and scaling
      imageTransform: {
        x: 0.5,      // Position as percentage (0-1), centered
        y: 0.5,      // Position as percentage (0-1), centered
        scale: 1.0   // Scale factor (0.3 to 2.0)
      },
      // Actual content bounds of the logo (non-white pixels)
      logoContentBounds: null,
      // Track if logo has been auto-sized for current image
      logoAutoSized: false,
      // Fit scale (scale at which logo exactly fills the boundary)
      logoFitScale: 1.0,
      // Cached full-resolution logo SVG paths (vectorized without scaling)
      fullResLogoSvg: null,
      // Dimensions of the full-res logo SVG for transform calculations
      fullResLogoDimensions: null,
      // Drag state for text and logo positioning
      dragging: {
        active: false,
        textLine: null,  // 'top', 'middle', or 'bottom'
        isLogo: false,   // true when dragging the logo
        startX: 0,
        startY: 0,
        offsetX: 0,      // Offset from grab point to object center
        offsetY: 0
      },
      quantities: {
        standard: 0,
        high: 0
      },
      finalDesignBlob: null,
      finalSvgString: null,
      textOnlyMode: false  // Text-only mode (no logo, no text line fees)
    };

    // ========== SVG Preview ==========
    // Step 2: instant raster preview + debounced vectorized preview
    // Steps 4-5: SVG preview only (final vector output)

    // Clear all cached SVGs when settings change
    function resetSvgPreview() {
      state.finalSvgString = null;
      state.fullResLogoSvg = null;
      state.previewSvg = null; // Clear debounced vector preview cache
      state.fullResLogoDimensions = null;
    }

    // ========== DOM Elements ==========
    const el = {
      // Progress
      steps: root.querySelectorAll('.stepper-step'),
      lines: root.querySelectorAll('.stepper-line'),
      panels: root.querySelectorAll('.stepper-panel'),
      btnsBack: root.querySelectorAll('.stepper-btn-back'),
      btnsNext: root.querySelectorAll('.stepper-btn-next'),

      // Step 1
      uploadZone: root.querySelector(`#upload-zone-${sectionId}`),
      uploadInput: root.querySelector(`#upload-input-${sectionId}`),
      uploadPreview: root.querySelector(`#upload-preview-${sectionId}`),
      originalPreview: root.querySelector(`#original-preview-${sectionId}`),
      convertedPreview: root.querySelector(`#converted-preview-${sectionId}`),
      changeImageBtn: root.querySelector(`#change-image-${sectionId}`),
      textOnlyBtn: root.querySelector(`#text-only-${sectionId}`),
      textOnlyFooterBtn: root.querySelector(`#text-only-footer-${sectionId}`),
      processingStatus: root.querySelector(`#processing-status-${sectionId}`),
      
      // Color selection
      colorModeCheckbox: root.querySelector(`#color-mode-${sectionId}`),
      colorPaletteContainer: root.querySelector(`#color-palette-container-${sectionId}`),
      colorPalette: root.querySelector(`#color-palette-${sectionId}`),
      colorCountSelect: root.querySelector(`#color-count-${sectionId}`),
      selectAllColorsBtn: root.querySelector(`#select-all-colors-${sectionId}`),
      deselectAllColorsBtn: root.querySelector(`#deselect-all-colors-${sectionId}`),
      invertColorsBtn: root.querySelector(`#invert-colors-${sectionId}`),

      // Step 2
      adjustCanvas: root.querySelector(`#adjust-canvas-${sectionId}`),
      thresholdSlider: root.querySelector(`#threshold-slider-${sectionId}`),
      thresholdValue: root.querySelector(`#threshold-value-${sectionId}`),
      thresholdLabel: root.querySelector(`#threshold-label-${sectionId}`),
      thresholdHint: root.querySelector(`#threshold-hint-${sectionId}`),
      contrastSlider: root.querySelector(`#contrast-slider-${sectionId}`),
      contrastValue: root.querySelector(`#contrast-value-${sectionId}`),
      contrastLabel: root.querySelector(`#contrast-label-${sectionId}`),
      contrastHint: root.querySelector(`#contrast-hint-${sectionId}`),
      fillGapsOn: root.querySelector(`#fill-gaps-on-${sectionId}`),
      fillGapsOff: root.querySelector(`#fill-gaps-off-${sectionId}`),
      gapFillSizeSlider: root.querySelector(`#gap-fill-size-slider-${sectionId}`),
      gapFillSizeValue: root.querySelector(`#gap-fill-size-value-${sectionId}`),
      gapFillSizeGroup: root.querySelector(`#gap-fill-size-group-${sectionId}`),
      invertBtn: root.querySelector(`#invert-btn-${sectionId}`),
      // Preprocessing controls
      blurSlider: root.querySelector(`#blur-slider-${sectionId}`),
      blurValue: root.querySelector(`#blur-value-${sectionId}`),
      brightnessSlider: root.querySelector(`#brightness-slider-${sectionId}`),
      brightnessValue: root.querySelector(`#brightness-value-${sectionId}`),
      despeckleSlider: root.querySelector(`#despeckle-slider-${sectionId}`),
      despeckleValue: root.querySelector(`#despeckle-value-${sectionId}`),
      antiAliasingOn: root.querySelector(`#aa-on-${sectionId}`),
      antiAliasingOff: root.querySelector(`#aa-off-${sectionId}`),
      antiAliasingHigh: root.querySelector(`#aa-high-${sectionId}`),
      // Preset buttons
      noiseOff: root.querySelector(`#noise-off-${sectionId}`),
      noiseLow: root.querySelector(`#noise-low-${sectionId}`),
      noiseHigh: root.querySelector(`#noise-high-${sectionId}`),
      detailFine: root.querySelector(`#detail-fine-${sectionId}`),
      detailBalanced: root.querySelector(`#detail-balanced-${sectionId}`),
      detailSimple: root.querySelector(`#detail-simple-${sectionId}`),
      adaptiveThreshold: root.querySelector(`#adaptive-threshold-${sectionId}`),
      // Vectorization controls
      turdsizeSlider: root.querySelector(`#turdsize-slider-${sectionId}`),
      turdsizeValue: root.querySelector(`#turdsize-value-${sectionId}`),
      cornerSlider: root.querySelector(`#corner-slider-${sectionId}`),
      cornerValue: root.querySelector(`#corner-value-${sectionId}`),
      optToleranceSlider: root.querySelector(`#opt-tolerance-slider-${sectionId}`),
      optToleranceValue: root.querySelector(`#opt-tolerance-value-${sectionId}`),
      turnPolicySelect: root.querySelector(`#turnpolicy-select-${sectionId}`),
      minDetailSlider: root.querySelector(`#min-detail-slider-${sectionId}`),
      minDetailValue: root.querySelector(`#min-detail-value-${sectionId}`),
      // Vectorize preview indicator
      vectorizeIndicator: root.querySelector(`#vectorize-indicator-${sectionId}`),

      // Step Guide
      stepperGuide: root.querySelector(`#stepper-guide-${sectionId}`),
      guideContent: root.querySelector(`#guide-content-${sectionId}`),
      guideToggle: root.querySelector(`#guide-toggle-${sectionId}`),

      // Step 3
      textPreview: root.querySelector(`#text-preview-${sectionId}`),
      textEnableTop: root.querySelector(`#text-top-enable-${sectionId}`),
      textEnableMiddle: root.querySelector(`#text-middle-enable-${sectionId}`),
      textEnableBottom: root.querySelector(`#text-bottom-enable-${sectionId}`),
      textFieldsTop: root.querySelector(`#text-top-fields-${sectionId}`),
      textFieldsMiddle: root.querySelector(`#text-middle-fields-${sectionId}`),
      textFieldsBottom: root.querySelector(`#text-bottom-fields-${sectionId}`),
      textInputTop: root.querySelector(`#text-top-input-${sectionId}`),
      textInputMiddle: root.querySelector(`#text-middle-input-${sectionId}`),
      textInputBottom: root.querySelector(`#text-bottom-input-${sectionId}`),
      textFontTop: root.querySelector(`#text-top-font-${sectionId}`),
      textFontMiddle: root.querySelector(`#text-middle-font-${sectionId}`),
      textFontBottom: root.querySelector(`#text-bottom-font-${sectionId}`),
      textLineCount: root.querySelector(`#text-line-count-${sectionId}`),
      textTotal: root.querySelector(`#text-total-${sectionId}`),
      resetPositions: root.querySelector(`#reset-positions-${sectionId}`),
      logoScaleSlider: root.querySelector(`#logo-scale-${sectionId}`),
      logoScaleValue: root.querySelector(`#logo-scale-value-${sectionId}`),
      logoScaleControl: root.querySelector(`#logo-scale-control-${sectionId}`),
      textDragHint: root.querySelector(`#text-drag-hint-${sectionId}`),
      step3Subtitle: root.querySelector(`#step3-subtitle-${sectionId}`),
      addTextLineBtn: root.querySelector(`#add-text-line-${sectionId}`),
      textLine1: root.querySelector(`#text-line-1-${sectionId}`),
      textLine2: root.querySelector(`#text-line-2-${sectionId}`),
      textLine3: root.querySelector(`#text-line-3-${sectionId}`),
      confirmStep3: root.querySelector(`#confirm-step3-${sectionId}`),

      // Step 4 - Dual SVG Preview
      productSvgPreviewStandard: root.querySelector(`#product-svg-preview-standard-${sectionId}`),
      productSvgPreviewHigh: root.querySelector(`#product-svg-preview-high-${sectionId}`),
      frostedOverlayStandard: root.querySelector(`#frosted-overlay-standard-${sectionId}`),
      frostedOverlayHigh: root.querySelector(`#frosted-overlay-high-${sectionId}`),

      // Step 5 - SVG Preview only
      loginRequired: root.querySelector(`#login-required-${sectionId}`),
      purchaseForm: root.querySelector(`#purchase-form-${sectionId}`),
      finalSvgPreview: root.querySelector(`#final-svg-preview-${sectionId}`),
      orderStandard: root.querySelector(`#order-standard-${sectionId}`),
      orderHigh: root.querySelector(`#order-high-${sectionId}`),
      orderText: root.querySelector(`#order-text-${sectionId}`),
      qtyStandard: root.querySelector(`#qty-standard-${sectionId}`),
      qtyHigh: root.querySelector(`#qty-high-${sectionId}`),
      priceStandard: root.querySelector(`#price-standard-${sectionId}`),
      priceHigh: root.querySelector(`#price-high-${sectionId}`),
      priceText: root.querySelector(`#price-text-${sectionId}`),
      textLinesSummary: root.querySelector(`#text-lines-summary-${sectionId}`),
      orderTotal: root.querySelector(`#order-total-${sectionId}`),
      addToCartBtn: root.querySelector(`#add-to-cart-${sectionId}`),
      cartStatus: root.querySelector(`#cart-status-${sectionId}`),
      downloadSvg: root.querySelector(`#download-svg-${sectionId}`),
      downloadPng: root.querySelector(`#download-png-${sectionId}`),
      downloadBoth: root.querySelector(`#download-both-${sectionId}`)
    };

    // Canvas contexts
    const ctx = {
      converted: el.convertedPreview?.getContext('2d', { willReadFrequently: true }),
      adjust: el.adjustCanvas?.getContext('2d', { willReadFrequently: true }),
      textPreview: el.textPreview?.getContext('2d', { willReadFrequently: true })
    };

    // ========== High-DPI Canvas Setup ==========
    // Scale canvases for sharp rendering on high-DPI displays
    function setupHighDpiCanvas(canvas, context) {
      if (!canvas || !context) return;
      
      // Set actual canvas size (more pixels for sharper display)
      canvas.width = CANVAS_SIZE;
      canvas.height = CANVAS_SIZE;
      
      // CSS width: 100% handles responsive display sizing
      // The higher internal resolution provides sharper rendering
      // Use crisp-edges for sharp B&W content display
      canvas.style.imageRendering = 'crisp-edges';
      canvas.style.webkitImageRendering = 'crisp-edges'; // Safari prefix
      
      // Enable high-quality image smoothing for source image scaling
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      
      // Scale context so drawing operations work at base size coordinates
      context.scale(DPR, DPR);
    }

    // Initialize all canvases for high-DPI
    function initHighDpiCanvases() {
      setupHighDpiCanvas(el.convertedPreview, ctx.converted);
      setupHighDpiCanvas(el.adjustCanvas, ctx.adjust);
      setupHighDpiCanvas(el.textPreview, ctx.textPreview);
    }

    // ========== Navigation ==========
    function goToStep(step) {
      if (step < 1 || step > 5) return;

      // Validate can proceed
      if (step > state.currentStep && !canProceed()) return;

      // Cancel any pending vectorized preview when leaving Step 2
      if (state.currentStep === 2 && step !== 2) {
        if (_vectorizeTimer) clearTimeout(_vectorizeTimer);
        if (el.vectorizeIndicator) el.vectorizeIndicator.style.display = 'none';
      }

      state.currentStep = step;
      updateUI();
    }

    function canProceed() {
      switch (state.currentStep) {
        case 1:
          // Allow proceeding if image uploaded OR text-only mode
          return state.originalImage !== null || state.textOnlyMode;
        case 2:
        case 3:
        case 4:
          return true;
        case 5:
          return config.customerLoggedIn;
        default:
          return true;
      }
    }

    // Update UI elements for text-only mode
    function updateTextOnlyUI() {
      if (state.textOnlyMode) {
        // Hide logo controls
        if (el.logoScaleControl) el.logoScaleControl.style.display = 'none';
        // Update drag hint text
        const hintText = el.textDragHint?.querySelector('.drag-hint-text');
        if (hintText) hintText.textContent = 'Drag text to reposition';
        // Update Step 3 subtitle
        if (el.step3Subtitle) {
          el.step3Subtitle.textContent = el.step3Subtitle.dataset.textOnly || 'Add up to 3 lines of text for your engraving. No additional charge.';
        }
      } else {
        // Show logo controls
        if (el.logoScaleControl) el.logoScaleControl.style.display = '';
        // Reset drag hint text
        const hintText = el.textDragHint?.querySelector('.drag-hint-text');
        if (hintText) hintText.textContent = 'Drag logo or text to reposition';
        // Reset Step 3 subtitle
        if (el.step3Subtitle) {
          el.step3Subtitle.textContent = el.step3Subtitle.dataset.defaultText || el.step3Subtitle.textContent;
        }
      }
    }

    // Auto-size logo to fit within bounds at 30% when entering Step 3
    function autoSizeLogoIfNeeded() {
      // Skip if already auto-sized for this image, in text-only mode, or no content bounds
      if (state.logoAutoSized || state.textOnlyMode || !state.logoContentBounds) return;
      
      const bounds = state.logoContentBounds;
      const contentWidth = bounds.width;
      const contentHeight = bounds.height;
      
      // Skip if content is invalid
      if (contentWidth <= 0 || contentHeight <= 0) return;
      
      // Calculate scale that would make content fit exactly within engrave boundary ("contain")
      const fitScaleX = ENGRAVE_BOUNDARY.width / contentWidth;
      const fitScaleY = ENGRAVE_BOUNDARY.height / contentHeight;
      const fitScale = Math.min(fitScaleX, fitScaleY);
      
      // Store fit scale for slider bounds
      state.logoFitScale = fitScale;
      
      // Start at 30% of the fit scale so logo is small and fully visible
      // Slider uses normalized range: 5% (min) to 100% (max = fits boundary)
      const initialSliderValue = 30; // 30% of fit scale
      
      // Calculate actual scale from slider value
      state.imageTransform.scale = fitScale * (initialSliderValue / 100);
      
      // Update slider: range 5-100, step 1, value represents % of fit scale
      if (el.logoScaleSlider) {
        el.logoScaleSlider.min = 5;
        el.logoScaleSlider.max = 100;
        el.logoScaleSlider.step = 1;
        el.logoScaleSlider.value = initialSliderValue;
      }
      if (el.logoScaleValue) el.logoScaleValue.textContent = `${initialSliderValue}%`;
      
      // Mark as auto-sized for this image
      state.logoAutoSized = true;
    }

    // ========== Step Guide Content ==========
    // Get guide image URLs from data attributes
    const guideImgColorful = root.dataset.guideImgColorful || '';
    const guideImgBw = root.dataset.guideImgBw || '';

    const guideContent = {
      1: `
        <h3>Step 1: Upload Your Image</h3>
        <p>Upload your logo or design to get started.</p>
        <p><strong>For best results:</strong></p>
        <ul>
          <li>Use a <strong>PNG file with a transparent background</strong> if possible</li>
          <li>High contrast images (clear blacks and whites) work best for engraving</li>
          <li>Simple designs with clean lines produce the sharpest engravings</li>
        </ul>
        <div class="guide-examples">
          <div class="guide-example">
            <img src="${guideImgColorful}" alt="Colorful logo example">
            <span class="guide-example-caption">Color image — needs Advanced Color Selection</span>
          </div>
          <div class="guide-example">
            <img src="${guideImgBw}" alt="Black and white logo example">
            <span class="guide-example-caption">Black & white — ready for standard processing</span>
          </div>
        </div>
        <div class="guide-section">
          <p class="guide-section-title">When to use Advanced Color Selection</p>
          <p>If your image has multiple colors (like the colorful example above), the standard conversion might not capture what you want. <strong>Advanced Color Selection</strong> detects colors in your image and lets you choose exactly which ones should be engraved.</p>
          <p class="guide-tip">Simply check the colors you want engraved, and uncheck the ones you don't.</p>
        </div>
        <p class="guide-tip">Tip: If your image has a busy background, don't worry — the next step will help you clean it up.</p>
      `,
      2: `
        <h3>Step 2: Adjust Your Image</h3>
        <p>Fine-tune your image so it's ready for engraving. Laser engraving works by etching dark areas onto your glasses. This step converts your image to black and white so only the important parts get engraved.</p>
        <div class="guide-options">
          <p class="guide-option"><span class="guide-option-title">Threshold</span> — Controls what becomes black (engraved) vs white (not engraved). Slide higher to remove more dark areas, lower to keep more detail.</p>
          <p class="guide-option"><span class="guide-option-title">Contrast</span> — Makes the difference between light and dark areas more distinct. Increase if your image looks washed out.</p>
          <p class="guide-option"><span class="guide-option-title">Invert Colors</span> — Swaps black and white. Use this if your design appears backwards (background being engraved instead of your logo).</p>
        </div>
      `,
      3: `
        <h3>Step 3: Position & Add Text</h3>
        <p>Place your design and add optional text lines.</p>
        <div class="guide-options">
          <p class="guide-option"><span class="guide-option-title">Logo Controls</span> — Drag your logo to position it. Use the slider to resize (5% to 100%). Click "Reset Position" to start over.</p>
          <p class="guide-option"><span class="guide-option-title">Text Lines</span> — Add up to 3 lines of custom text. Choose from various fonts, sizes, and styles. Drag text to position it exactly where you want.</p>
        </div>
        <p class="guide-tip">The dotted square shows the engraving boundary — keep your design inside for best results.</p>
      `,
      4: `
        <h3>Step 4: Preview Your Design</h3>
        <p>Review your final engraving before ordering. This is how your design will look when engraved.</p>
        <p><strong>Take a moment to check:</strong></p>
        <ul>
          <li>Is your logo positioned correctly?</li>
          <li>Is all text spelled correctly and readable?</li>
          <li>Are the right parts of your image being engraved?</li>
        </ul>
        <p class="guide-tip">If something doesn't look right, use the Back button to make adjustments.</p>
      `,
      5: `
        <h3>Step 5: Complete Your Order</h3>
        <p>Choose your quantities and add to cart. Select how many pairs you'd like with this custom engraving. Your design files will be saved with your order.</p>
      `
    };

    function updateStepGuide() {
      if (!el.guideContent) return;
      const content = guideContent[state.currentStep] || '';
      el.guideContent.innerHTML = content;
    }

    function updateUI() {
      // Update step guide
      updateStepGuide();
      
      // Update progress indicator
      el.steps.forEach((stepEl, index) => {
        const stepNum = index + 1;
        stepEl.classList.remove('active', 'completed');
        if (stepNum === state.currentStep) {
          stepEl.classList.add('active');
        } else if (state.completedSteps.has(stepNum)) {
          stepEl.classList.add('completed');
        }
      });

      el.lines.forEach((line, index) => {
        const afterStep = index + 1;
        line.classList.toggle('completed', state.completedSteps.has(afterStep));
      });

      // Update panels
      el.panels.forEach(panel => {
        const panelNum = parseInt(panel.dataset.panel);
        panel.style.display = panelNum === state.currentStep ? 'block' : 'none';
      });

      // Update nav buttons (all instances)
      el.btnsBack.forEach(btn => {
        btn.disabled = state.currentStep === 1;
      });
      
      el.btnsNext.forEach(btn => {
        if (state.currentStep === 5) {
          btn.style.display = 'none';
        } else {
          btn.style.display = '';
          btn.disabled = !canProceed();
        }
      });

      // Step-specific updates
      if (state.currentStep === 2) {
        // In color mode, reset sliders to neutral so Step 2 matches Step 1's preview
        // Only do this once per color mode session
        if (state.colorModeEnabled && !state.colorModeSlidersReset) {
          resetSlidersForColorMode();
          state.colorModeSlidersReset = true;
        }
        updateSliderLabels();
        renderAdjustPreview();
        resetSvgPreview(); // Show vector preview after entering Step 2
      } else if (state.currentStep === 3) {
        updateTextOnlyUI(); // Show/hide logo controls based on text-only mode
        autoSizeLogoIfNeeded(); // Auto-size logo to 30% of fit size on first entry
        renderTextPreview();
        resetSvgPreview(); // Clear cached SVG
      } else if (state.currentStep === 4) {
        renderProductSvgPreview(); // SVG only for Step 4
      } else if (state.currentStep === 5) {
        renderFinalSvgPreview(); // SVG only for Step 5
        updateOrderSummary();
      }
    }

    // ========== Dynamic Slider Labels ==========
    // Update slider labels based on whether color selection mode is enabled
    function updateSliderLabels() {
      if (state.colorModeEnabled) {
        // Color selection mode: threshold becomes color tolerance, contrast helps separate colors
        if (el.thresholdLabel) el.thresholdLabel.textContent = 'Color Tolerance';
        if (el.thresholdHint) el.thresholdHint.textContent = 'Higher = stricter color matching';
        if (el.contrastLabel) el.contrastLabel.textContent = 'Color Separation';
        if (el.contrastHint) el.contrastHint.textContent = 'Enhance separation between colors';
      } else {
        // Standard luminance mode
        if (el.thresholdLabel) el.thresholdLabel.textContent = 'Threshold';
        if (el.thresholdHint) el.thresholdHint.textContent = 'Higher = less black, Lower = more black';
        if (el.contrastLabel) el.contrastLabel.textContent = 'Contrast';
        if (el.contrastHint) el.contrastHint.textContent = 'Enhance image contrast';
      }
    }
    
    // Reset sliders to neutral values for color mode
    // This ensures Step 2 starts looking identical to Step 1's preview
    function resetSlidersForColorMode() {
      // Set neutral values: no contrast enhancement, maximum tolerance (no filtering)
      state.contrast = 1.0;
      state.threshold = 0;
      
      // Update slider UI elements
      if (el.contrastSlider) {
        el.contrastSlider.value = 1.0;
      }
      if (el.contrastValue) {
        el.contrastValue.textContent = '1.0';
      }
      if (el.thresholdSlider) {
        el.thresholdSlider.value = 0;
      }
      if (el.thresholdValue) {
        el.thresholdValue.textContent = '0';
      }
    }
    
    // Reset sliders to standard defaults for luminance threshold mode
    // Called when disabling color mode to restore normal threshold processing
    function resetSlidersToStandardDefaults() {
      state.contrast = 1.3;
      state.threshold = 165;
      
      // Update slider UI elements
      if (el.contrastSlider) {
        el.contrastSlider.value = 1.3;
      }
      if (el.contrastValue) {
        el.contrastValue.textContent = '1.3';
      }
      if (el.thresholdSlider) {
        el.thresholdSlider.value = 165;
      }
      if (el.thresholdValue) {
        el.thresholdValue.textContent = '165';
      }
    }

    // ========== Image Processing ==========
    // Full preprocessing pipeline for raster B&W conversion (instant preview)
    function processImage(sourceCanvas, targetCtx, targetCanvas) {
      // Work in base size coordinates (context is scaled by DPR)
      const w = BASE_SIZE, h = BASE_SIZE;

      // Clear canvas and fill with white background (prevents black letterbox bars)
      targetCtx.clearRect(0, 0, w, h);
      targetCtx.fillStyle = '#ffffff';
      targetCtx.fillRect(0, 0, w, h);

      if (sourceCanvas instanceof HTMLImageElement) {
        const scale = Math.min(w / sourceCanvas.naturalWidth, h / sourceCanvas.naturalHeight);
        const dw = sourceCanvas.naturalWidth * scale;
        const dh = sourceCanvas.naturalHeight * scale;
        const dx = (w - dw) / 2;
        const dy = (h - dh) / 2;
        targetCtx.drawImage(sourceCanvas, dx, dy, dw, dh);
      } else {
        targetCtx.drawImage(sourceCanvas, 0, 0, w, h);
      }

      // For pixel manipulation, work with actual canvas pixels
      targetCtx.save();
      targetCtx.setTransform(1, 0, 0, 1, 0, 0);

      const actualW = targetCanvas.width, actualH = targetCanvas.height;
      const imgData = targetCtx.getImageData(0, 0, actualW, actualH);
      const d = imgData.data;

      // 1. Gaussian blur (before everything)
      if (state.blurRadius > 0) {
        applyGaussianBlur(imgData, actualW, actualH, state.blurRadius);
      }

      // 2. Brightness
      if (state.brightness !== 0) {
        const br = state.brightness;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] < 10) continue;
          for (let c = 0; c < 3; c++) {
            d[i + c] = Math.max(0, Math.min(255, d[i + c] + br));
          }
        }
      }

      // 3. Contrast
      const contrast = state.contrast;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 10) continue;
        for (let c = 0; c < 3; c++) {
          let val = d[i + c];
          val = ((val / 255 - 0.5) * contrast + 0.5) * 255;
          d[i + c] = Math.max(0, Math.min(255, val));
        }
      }

      // 4. Threshold (luminance to B&W)
      if (state.thresholdMode === 'adaptive') {
        applySauvolaThreshold(imgData, actualW, actualH, state.sauvolaWindowSize, state.sauvolaK);
      } else {
        const threshold = state.threshold;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] < 10) {
            d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; d[i + 3] = 255;
            continue;
          }
          const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          let isBlack = lum < threshold;
          if (state.inverted) isBlack = !isBlack;

          if (isBlack) {
            d[i] = 0; d[i + 1] = 0; d[i + 2] = 0; d[i + 3] = 255;
          } else {
            d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; d[i + 3] = 255;
          }
        }
      }

      // 5. Anti-aliasing (smooth edges)
      if (state.antiAliasing) {
        applySmoothEdges(imgData, actualW, actualH);
      }

      // 6. Despeckle (remove small black spots)
      if (state.despeckleSize > 0) {
        applyDespeckle(imgData, actualW, actualH, state.despeckleSize);
      }

      // 7. Fill gaps
      if (state.fillGaps) {
        applySelectiveGapFill(imgData, actualW, actualH, state.gapFillSize);
      }

      targetCtx.putImageData(imgData, 0, 0);
      targetCtx.restore();

      state.processedImageData = imgData;

      // Calculate and store the actual content bounds (non-white pixels)
      state.logoContentBounds = calculateContentBoundsFromImageData(imgData);
    }
    
    // Calculate content bounds from ImageData (finds bounding box of non-white pixels)
    function calculateContentBoundsFromImageData(imgData) {
      const w = imgData.width;
      const h = imgData.height;
      const d = imgData.data;
      
      let minX = w, minY = h, maxX = 0, maxY = 0;
      let foundContent = false;
      
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = (y * w + x) * 4;
          // Check if pixel is not white/transparent (has content)
          const r = d[i], g = d[i + 1], b = d[i + 2], a = d[i + 3];
          // Consider it content if it's not fully transparent and not white
          if (a > 10 && (r < 250 || g < 250 || b < 250)) {
            foundContent = true;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      
      if (!foundContent) {
        // No content found, return full canvas
        return { minX: 0, minY: 0, maxX: w - 1, maxY: h - 1, width: w, height: h };
      }
      
      return {
        minX: minX,
        minY: minY,
        maxX: maxX,
        maxY: maxY,
        width: maxX - minX + 1,
        height: maxY - minY + 1
      };
    }

    function renderAdjustPreview() {
      if (!state.originalImage) return;

      if (state.colorModeEnabled && state.detectedColors.length > 0) {
        // Use color-aware processing with slider adjustments (Step 2)
        processImageWithColorsOnCanvas(ctx.adjust, el.adjustCanvas, true);
      } else {
        // Use standard threshold processing for instant raster feedback
        processImage(state.originalImage, ctx.adjust, el.adjustCanvas);
      }

      // Schedule debounced vectorized preview (replaces raster with SVG)
      scheduleVectorizedPreview();
    }

    // ========== Color Extraction (Median Cut Algorithm) ==========
    function extractColors(imageElement, numColors) {
      // Create temporary canvas to get pixel data
      const canvas = document.createElement('canvas');
      const size = 200; // Sample at lower resolution for speed
      canvas.width = size;
      canvas.height = size;
      const ctx2d = canvas.getContext('2d');
      
      ctx2d.drawImage(imageElement, 0, 0, size, size);
      const imgData = ctx2d.getImageData(0, 0, size, size);
      const pixels = [];
      
      // Collect all non-transparent pixels
      for (let i = 0; i < imgData.data.length; i += 4) {
        if (imgData.data[i + 3] > 128) { // Skip transparent
          pixels.push({
            r: imgData.data[i],
            g: imgData.data[i + 1],
            b: imgData.data[i + 2]
          });
        }
      }
      
      // Median cut quantization
      const colors = medianCut(pixels, numColors);
      
      // Sort by luminance (dark to light)
      colors.sort((a, b) => {
        const lumA = 0.299 * a.r + 0.587 * a.g + 0.114 * a.b;
        const lumB = 0.299 * b.r + 0.587 * b.g + 0.114 * b.b;
        return lumA - lumB;
      });
      
      // Add hex and selection state
      return colors.map(c => ({
        ...c,
        hex: rgbToHex(c.r, c.g, c.b),
        selected: true // All selected by default
      }));
    }
    
    function medianCut(pixels, numColors) {
      if (pixels.length === 0) return [];
      
      let buckets = [pixels];
      
      while (buckets.length < numColors) {
        // Find bucket with largest range
        let maxRange = 0;
        let maxBucketIdx = 0;
        let maxChannel = 'r';
        
        buckets.forEach((bucket, idx) => {
          if (bucket.length < 2) return;
          
          ['r', 'g', 'b'].forEach(ch => {
            const vals = bucket.map(p => p[ch]);
            const range = Math.max(...vals) - Math.min(...vals);
            if (range > maxRange) {
              maxRange = range;
              maxBucketIdx = idx;
              maxChannel = ch;
            }
          });
        });
        
        // Stop splitting if color differences are too small (threshold of 5 RGB units)
        if (maxRange < 5) break;
        
        // Split the bucket
        const bucket = buckets[maxBucketIdx];
        bucket.sort((a, b) => a[maxChannel] - b[maxChannel]);
        const mid = Math.floor(bucket.length / 2);
        
        buckets.splice(maxBucketIdx, 1, bucket.slice(0, mid), bucket.slice(mid));
      }
      
      // Calculate average color for each bucket
      return buckets.map(bucket => {
        if (bucket.length === 0) return { r: 0, g: 0, b: 0, count: 0 };
        
        const sum = bucket.reduce((acc, p) => ({
          r: acc.r + p.r,
          g: acc.g + p.g,
          b: acc.b + p.b
        }), { r: 0, g: 0, b: 0 });
        
        return {
          r: Math.round(sum.r / bucket.length),
          g: Math.round(sum.g / bucket.length),
          b: Math.round(sum.b / bucket.length),
          count: bucket.length
        };
      }).filter(c => c.count > 0);
    }
    
    function rgbToHex(r, g, b) {
      return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
    }
    
    function colorDistance(c1, c2) {
      // Simple Euclidean distance in RGB space
      return Math.sqrt(
        Math.pow(c1.r - c2.r, 2) +
        Math.pow(c1.g - c2.g, 2) +
        Math.pow(c1.b - c2.b, 2)
      );
    }
    
    function findClosestColor(pixel, colors) {
      let minDist = Infinity;
      let closest = null;
      
      colors.forEach(c => {
        const dist = colorDistance(pixel, c);
        if (dist < minDist) {
          minDist = dist;
          closest = c;
        }
      });
      
      return closest;
    }
    
    // ========== Selective Gap Filling ==========
    // Fills only small isolated white pixels while preserving intentional white outlines
    // Uses connected component analysis to identify small white regions
    function applySelectiveGapFill(imgData, w, h, maxGapSize = 50) {
      const d = imgData.data;
      
      // Create binary representation (1 = white, 0 = black)
      const binary = new Uint8Array(w * h);
      for (let i = 0; i < w * h; i++) {
        binary[i] = d[i * 4] >= 128 ? 1 : 0;
      }
      
      // Find connected white regions and their sizes
      const visited = new Uint8Array(w * h);
      const regionMap = new Int32Array(w * h); // Maps pixel to region ID
      const regionSizes = [0]; // Index 0 unused, region IDs start at 1
      let regionId = 0;
      
      // Flood fill to find connected white regions
      function floodFill(startX, startY) {
        if (binary[startY * w + startX] === 0) return 0; // Not white
        if (visited[startY * w + startX]) return 0; // Already visited
        
        regionId++;
        const stack = [[startX, startY]];
        let size = 0;
        
        while (stack.length > 0) {
          const [x, y] = stack.pop();
          const idx = y * w + x;
          
          if (x < 0 || x >= w || y < 0 || y >= h) continue;
          if (visited[idx]) continue;
          if (binary[idx] === 0) continue; // Not white
          
          visited[idx] = 1;
          regionMap[idx] = regionId;
          size++;
          
          // Add 4-connected neighbors
          stack.push([x + 1, y]);
          stack.push([x - 1, y]);
          stack.push([x, y + 1]);
          stack.push([x, y - 1]);
        }
        
        return size;
      }
      
      // Find all white regions
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const size = floodFill(x, y);
          if (size > 0) {
            regionSizes.push(size);
          }
        }
      }
      
      // Fill only small white regions (isolated gaps)
      for (let i = 0; i < w * h; i++) {
        const region = regionMap[i];
        if (region > 0 && regionSizes[region] <= maxGapSize) {
          // This is a small isolated white region - fill it with black
          d[i * 4] = 0;
          d[i * 4 + 1] = 0;
          d[i * 4 + 2] = 0;
          d[i * 4 + 3] = 255;
        }
      }
    }
    
    // Smooth edges after gap fill to restore anti-aliasing for clean vector output
    function applySmoothEdges(imgData, w, h) {
      const d = imgData.data;
      const src = new Uint8Array(w * h);
      
      // Copy current values (grayscale)
      for (let i = 0; i < w * h; i++) {
        src[i] = d[i * 4];
      }
      
      // Apply 3x3 Gaussian blur to smooth edges
      const kernel = [
        1, 2, 1,
        2, 4, 2,
        1, 2, 1
      ];
      const kernelSum = 16;
      
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          let sum = 0;
          let ki = 0;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              sum += src[(y + dy) * w + (x + dx)] * kernel[ki++];
            }
          }
          const blurred = sum / kernelSum;
          
          // Apply soft threshold to create smooth anti-aliased edges
          // Values near 0 stay black, values near 255 stay white
          // Values in between get a smooth gradient
          let output;
          if (blurred < 64) {
            output = 0;
          } else if (blurred > 192) {
            output = 255;
          } else {
            // Smooth transition zone
            output = (blurred - 64) * (255 / 128);
          }
          
          const idx = (y * w + x) * 4;
          d[idx] = output;
          d[idx + 1] = output;
          d[idx + 2] = output;
          d[idx + 3] = 255;
        }
      }
    }

    // ========== Gaussian Blur (Separable 2-Pass) ==========
    // Applies a Gaussian blur to ImageData with given radius
    function applyGaussianBlur(imgData, w, h, radius) {
      if (radius < 0.5) return; // No-op for tiny radius
      const d = imgData.data;
      const sigma = radius * 0.65;
      const kernelSize = Math.ceil(radius * 3) * 2 + 1;
      const half = Math.floor(kernelSize / 2);

      // Build 1D Gaussian kernel
      const kernel = new Float32Array(kernelSize);
      let sum = 0;
      for (let i = 0; i < kernelSize; i++) {
        const x = i - half;
        kernel[i] = Math.exp(-(x * x) / (2 * sigma * sigma));
        sum += kernel[i];
      }
      for (let i = 0; i < kernelSize; i++) kernel[i] /= sum;

      // Temp buffer for intermediate results
      const tmp = new Float32Array(w * h * 3);

      // Horizontal pass
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          let r = 0, g = 0, b = 0;
          for (let k = -half; k <= half; k++) {
            const sx = Math.min(w - 1, Math.max(0, x + k));
            const si = (y * w + sx) * 4;
            const kv = kernel[k + half];
            r += d[si] * kv;
            g += d[si + 1] * kv;
            b += d[si + 2] * kv;
          }
          const ti = (y * w + x) * 3;
          tmp[ti] = r;
          tmp[ti + 1] = g;
          tmp[ti + 2] = b;
        }
      }

      // Vertical pass
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          let r = 0, g = 0, b = 0;
          for (let k = -half; k <= half; k++) {
            const sy = Math.min(h - 1, Math.max(0, y + k));
            const ti = (sy * w + x) * 3;
            const kv = kernel[k + half];
            r += tmp[ti] * kv;
            g += tmp[ti + 1] * kv;
            b += tmp[ti + 2] * kv;
          }
          const di = (y * w + x) * 4;
          if (d[di + 3] < 10) continue; // Skip transparent pixels
          d[di] = Math.round(r);
          d[di + 1] = Math.round(g);
          d[di + 2] = Math.round(b);
        }
      }
    }

    // ========== Edge Pixel Perturbation (VTracer Crash Prevention) ==========
    // Randomly flips a small fraction of edge pixels (black↔white) to break
    // perfectly parallel contour segments that crash VTracer's path-fitting.
    // Only used as a retry mechanism — clean data is tried first.
    function perturbEdgePixels(imgData, w, h, flipRate) {
      flipRate = flipRate || 0.015;
      const d = imgData.data;
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = (y * w + x) * 4;
          const isBlack = d[i] < 128;
          // Check 4-connected neighbors for a pixel of opposite color
          const n = ((y - 1) * w + x) * 4;
          const s = ((y + 1) * w + x) * 4;
          const ww = (y * w + x - 1) * 4;
          const e = (y * w + x + 1) * 4;
          const hasOpposite = isBlack
            ? (d[n] >= 128 || d[s] >= 128 || d[ww] >= 128 || d[e] >= 128)
            : (d[n] < 128 || d[s] < 128 || d[ww] < 128 || d[e] < 128);
          if (hasOpposite && Math.random() < flipRate) {
            const v = isBlack ? 255 : 0;
            d[i] = v; d[i + 1] = v; d[i + 2] = v;
          }
        }
      }
    }

    // ========== Despeckle (Remove Small Black Regions) ==========
    // Inverse of applySelectiveGapFill — removes small isolated black regions
    function applyDespeckle(imgData, w, h, minIslandSize) {
      if (minIslandSize <= 0) return;
      const d = imgData.data;

      // Create binary representation (1 = black, 0 = white/transparent)
      const binary = new Uint8Array(w * h);
      for (let i = 0; i < w * h; i++) {
        binary[i] = (d[i * 4 + 3] > 10 && d[i * 4] < 128) ? 1 : 0;
      }

      const visited = new Uint8Array(w * h);
      const regionMap = new Int32Array(w * h);
      const regionSizes = [0];
      let regionId = 0;

      function floodFillBlack(startX, startY) {
        if (binary[startY * w + startX] === 0) return 0;
        if (visited[startY * w + startX]) return 0;

        regionId++;
        const stack = [[startX, startY]];
        let size = 0;

        while (stack.length > 0) {
          const [x, y] = stack.pop();
          const idx = y * w + x;
          if (x < 0 || x >= w || y < 0 || y >= h) continue;
          if (visited[idx]) continue;
          if (binary[idx] === 0) continue;

          visited[idx] = 1;
          regionMap[idx] = regionId;
          size++;

          stack.push([x + 1, y]);
          stack.push([x - 1, y]);
          stack.push([x, y + 1]);
          stack.push([x, y - 1]);
        }
        return size;
      }

      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const size = floodFillBlack(x, y);
          if (size > 0) regionSizes.push(size);
        }
      }

      // Remove small black regions (turn them white/transparent)
      for (let i = 0; i < w * h; i++) {
        const region = regionMap[i];
        if (region > 0 && regionSizes[region] <= minIslandSize) {
          d[i * 4] = 255;
          d[i * 4 + 1] = 255;
          d[i * 4 + 2] = 255;
          d[i * 4 + 3] = 0;
        }
      }
    }

    // ========== Sauvola Adaptive Thresholding ==========
    // Per-pixel threshold based on local mean + standard deviation
    // Uses integral images (summed area tables) for O(n) computation
    function buildIntegralImages(d, w, h) {
      const n = w * h;
      const sum = new Float64Array(n);
      const sumSq = new Float64Array(n);

      for (var y = 0; y < h; y++) {
        var rowSum = 0, rowSumSq = 0;
        for (var x = 0; x < w; x++) {
          var idx = y * w + x;
          var lum = 0.299 * d[idx * 4] + 0.587 * d[idx * 4 + 1] + 0.114 * d[idx * 4 + 2];
          rowSum += lum;
          rowSumSq += lum * lum;
          if (y > 0) {
            sum[idx] = sum[(y - 1) * w + x] + rowSum;
            sumSq[idx] = sumSq[(y - 1) * w + x] + rowSumSq;
          } else {
            sum[idx] = rowSum;
            sumSq[idx] = rowSumSq;
          }
        }
      }
      return { sum: sum, sumSq: sumSq };
    }

    function integralRectSum(integral, w, x1, y1, x2, y2) {
      var a = (y1 > 0 && x1 > 0) ? integral[(y1 - 1) * w + (x1 - 1)] : 0;
      var b = (y1 > 0) ? integral[(y1 - 1) * w + x2] : 0;
      var c = (x1 > 0) ? integral[y2 * w + (x1 - 1)] : 0;
      var d = integral[y2 * w + x2];
      return d - b - c + a;
    }

    function applySauvolaThreshold(imgData, w, h, windowSize, k) {
      windowSize = windowSize || 25;
      k = k || 0.3;
      var R = 128; // Dynamic range of standard deviation
      var halfWin = Math.floor(windowSize / 2);
      var d = imgData.data;

      var integrals = buildIntegralImages(d, w, h);
      var sumImg = integrals.sum;
      var sumSqImg = integrals.sumSq;

      // Pre-compute grayscale for reading (threshold overwrites in-place)
      var gray = new Float32Array(w * h);
      for (var i = 0; i < w * h; i++) {
        gray[i] = 0.299 * d[i * 4] + 0.587 * d[i * 4 + 1] + 0.114 * d[i * 4 + 2];
      }

      for (var y = 0; y < h; y++) {
        var y1 = Math.max(0, y - halfWin);
        var y2 = Math.min(h - 1, y + halfWin);
        for (var x = 0; x < w; x++) {
          var x1 = Math.max(0, x - halfWin);
          var x2 = Math.min(w - 1, x + halfWin);
          var count = (x2 - x1 + 1) * (y2 - y1 + 1);

          var localSum = integralRectSum(sumImg, w, x1, y1, x2, y2);
          var localSumSq = integralRectSum(sumSqImg, w, x1, y1, x2, y2);

          var mean = localSum / count;
          var variance = (localSumSq / count) - (mean * mean);
          var stdev = Math.sqrt(Math.max(0, variance));

          var threshold = mean * (1 + k * (stdev / R - 1));

          var idx = y * w + x;
          var pi = idx * 4;
          var lum = gray[idx];

          var isBlack = lum < threshold;
          if (state.inverted) isBlack = !isBlack;

          if (isBlack) {
            d[pi] = 0; d[pi + 1] = 0; d[pi + 2] = 0; d[pi + 3] = 255;
          } else {
            d[pi] = 255; d[pi + 1] = 255; d[pi + 2] = 255; d[pi + 3] = 255;
          }
        }
      }
    }

    // ========== Auto-Upscale Helper ==========
    // Creates an upscaled canvas from the source image, capped at 3000x3000
    function createUpscaledCanvas(sourceImage) {
      const natW = sourceImage.naturalWidth;
      const natH = sourceImage.naturalHeight;
      const maxDim = 3000;
      const scaleFactor = Math.max(1, Math.floor(Math.min(maxDim / natW, maxDim / natH)));
      const w = natW * scaleFactor;
      const h = natH * scaleFactor;

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx2d = canvas.getContext('2d');
      ctx2d.imageSmoothingEnabled = true;
      ctx2d.imageSmoothingQuality = 'high';
      ctx2d.drawImage(sourceImage, 0, 0, w, h);

      return { canvas, ctx: ctx2d, scale: scaleFactor, width: w, height: h };
    }

    // ========== Preprocessing Pipeline (on upscaled canvas) ==========
    // Runs all preprocessing on an offscreen canvas, returns preprocessed ImageData
    function preprocessFullRes() {
      if (!state.originalImage) return null;

      // Create or reuse upscaled canvas
      if (!state.upscaledCanvas) {
        const info = createUpscaledCanvas(state.originalImage);
        state.upscaledCanvas = info.canvas;
        state.upscaleInfo = { scale: info.scale, width: info.width, height: info.height };
      }

      const srcCanvas = state.upscaledCanvas;
      const w = srcCanvas.width;
      const h = srcCanvas.height;

      // Create working canvas
      const workCanvas = document.createElement('canvas');
      workCanvas.width = w;
      workCanvas.height = h;
      const workCtx = workCanvas.getContext('2d');
      workCtx.drawImage(srcCanvas, 0, 0);

      const imgData = workCtx.getImageData(0, 0, w, h);
      const d = imgData.data;

      // 1. Gaussian blur (before everything else)
      if (state.blurRadius > 0) {
        applyGaussianBlur(imgData, w, h, state.blurRadius);
      }

      // 2. Brightness
      if (state.brightness !== 0) {
        const br = state.brightness;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] < 10) continue;
          for (let c = 0; c < 3; c++) {
            d[i + c] = Math.max(0, Math.min(255, d[i + c] + br));
          }
        }
      }

      // 3. Contrast
      const contrast = state.contrast;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 10) continue;
        for (let c = 0; c < 3; c++) {
          let val = d[i + c];
          val = ((val / 255 - 0.5) * contrast + 0.5) * 255;
          d[i + c] = Math.max(0, Math.min(255, val));
        }
      }

      // 4. Threshold (luminance to B&W)
      if (state.thresholdMode === 'adaptive') {
        applySauvolaThreshold(imgData, w, h, state.sauvolaWindowSize, state.sauvolaK);
      } else {
        const threshold = state.threshold;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] < 10) {
            d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; d[i + 3] = 255;
            continue;
          }
          const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          let isBlack = lum < threshold;
          if (state.inverted) isBlack = !isBlack;

          if (isBlack) {
            d[i] = 0; d[i + 1] = 0; d[i + 2] = 0; d[i + 3] = 255;
          } else {
            d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; d[i + 3] = 255;
          }
        }
      }

      // 5. Anti-aliasing (smooth edges)
      if (state.antiAliasing) {
        applySmoothEdges(imgData, w, h);
      }

      // 6. Despeckle (remove small black spots)
      if (state.despeckleSize > 0) {
        applyDespeckle(imgData, w, h, state.despeckleSize);
      }

      // 7. Fill gaps (fill small white holes)
      if (state.fillGaps) {
        applySelectiveGapFill(imgData, w, h, state.gapFillSize);
      }

      workCtx.putImageData(imgData, 0, 0);
      return workCanvas;
    }

    // ========== Debounced Vectorized Preview ==========
    // Runs Potrace on preprocessed image and renders SVG to Step 2 canvas
    let _vectorizeTimer = null;
    let _vectorizing = false;

    function scheduleVectorizedPreview() {
      if (_vectorizeTimer) clearTimeout(_vectorizeTimer);
      // Show loading indicator
      if (el.vectorizeIndicator) el.vectorizeIndicator.style.display = '';
      _vectorizeTimer = setTimeout(runVectorizedPreview, 300);
    }

    function runVectorizedPreview() {
      if (!state.originalImage || state.currentStep !== 2) return;
      // Need at least one vectorizer available
      if (!window.VTracer && !window.Potrace) return;
      if (_vectorizing) {
        scheduleVectorizedPreview();
        return;
      }
      _vectorizing = true;

      const preprocessed = preprocessFullRes();
      if (!preprocessed) {
        _vectorizing = false;
        return;
      }

      const imgData = preprocessed.getContext('2d').getImageData(0, 0, preprocessed.width, preprocessed.height);

      vectorizeImageData(imgData, {}, function(svg) {
        state.previewSvg = svg;
        renderSvgToAdjustCanvas(svg);
        _vectorizing = false;
        if (el.vectorizeIndicator) {
          el.vectorizeIndicator.style.display = 'none';
          el.vectorizeIndicator.textContent = 'Vectorizing...';
        }
      });
    }

    // ========== VTracer Integration ==========
    // Vectorize ImageData using VTracer WASM (primary) or Potrace (fallback)
    function vectorizeImageData(imageData, params, callback) {
      params = params || {};
      var _vecStart = performance.now();

      // Try VTracer first (WASM, MIT license, O(n) algorithm)
      // Uses try-retry: clean first, then with edge perturbation if parallel panic
      if (window.VTracer && window.VTracer.BinaryImageConverter) {
        var converterOptions = {
          colorMode: 'bw',
          filterSpeckle: params.filterSpeckle || state.turdsize || 4,
          cornerThreshold: params.cornerThreshold || Math.round(state.cornerSharpness * 67),
          lengthThreshold: params.lengthThreshold || Math.max(2, state.curveOptimization * 5),
          spliceThreshold: params.spliceThreshold || 45,
          pathPrecision: params.pathPrecision || 3
        };
        var vtOptions = { invert: false, pathColor: '#000000' };

        var attempts = [
          { perturb: false, label: 'clean' },
          { perturb: true,  label: 'perturbed' }
        ];
        var vtSuccess = false;

        for (var a = 0; a < attempts.length; a++) {
          try {
            var vtImageData = new ImageData(
              new Uint8ClampedArray(imageData.data),
              imageData.width, imageData.height
            );

            if (attempts[a].perturb) {
              perturbEdgePixels(vtImageData, imageData.width, imageData.height, 0.015);
            }

            console.log('%c[Vectorizer] Using VTracer WASM (' + attempts[a].label + ')', 'color: #00aa00; font-weight: bold',
              { input: imageData.width + 'x' + imageData.height, params: converterOptions });

            var converter = new VTracer.BinaryImageConverter(vtImageData, converterOptions, vtOptions);
            converter.init();

            // Run tick loop (synchronous but WASM-fast)
            while (converter.tick()) {
              if (el.vectorizeIndicator) {
                var pct = converter.progress();
                if (pct > 0) el.vectorizeIndicator.textContent = 'Vectorizing... ' + pct + '%';
              }
            }

            var svg = converter.getResult();
            converter.free();

            var _vecMs = (performance.now() - _vecStart).toFixed(1);
            var pathCount = (svg.match(/<path/g) || []).length;
            console.log('%c[Vectorizer] VTracer complete (' + attempts[a].label + ')', 'color: #00aa00; font-weight: bold',
              { time: _vecMs + 'ms', paths: pathCount, svgSize: (svg.length / 1024).toFixed(1) + 'KB' });

            // Post-process: remove small detail paths
            if (state.minDetailSize > 0) {
              svg = postProcessSvg(svg, state.minDetailSize);
            }

            callback(svg);
            return;
          } catch (err) {
            var isParallelPanic = err.message && err.message.toLowerCase().indexOf('parallel') !== -1;
            if (a === 0 && isParallelPanic) {
              console.warn('%c[Vectorizer] Parallel lines panic detected, retrying with edge perturbation...',
                'color: #ff6600; font-weight: bold');
              continue; // retry with perturbation
            }
            console.warn('%c[Vectorizer] VTracer failed, falling back to Potrace:', 'color: #ff6600; font-weight: bold', err.message);
            break; // fall through to Potrace
          }
        }
      }

      // Fallback to Potrace (GPL, pure JS, O(n²))
      if (window.Potrace) {
        console.log('%c[Vectorizer] Using Potrace (fallback)', 'color: #cc6600; font-weight: bold',
          { input: imageData.width + 'x' + imageData.height, reason: window.VTracer ? 'VTracer error' : 'VTracer not loaded' });
        // Potrace needs a data URL, not ImageData — render to temp canvas
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = imageData.width;
        tempCanvas.height = imageData.height;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.putImageData(imageData, 0, 0);
        const dataUrl = tempCanvas.toDataURL('image/png');

        Potrace.loadImageFromUrl(dataUrl);
        Potrace.setParameter({
          turdsize: state.turdsize,
          alphamax: state.cornerSharpness,
          optcurve: true,
          opttolerance: state.curveOptimization,
          turnpolicy: state.turnPolicy
        });
        Potrace.process(function() {
          let svg = Potrace.getSVG(1);
          var _vecMs = (performance.now() - _vecStart).toFixed(1);
          var pathCount = (svg.match(/<path/g) || []).length;
          console.log('%c[Vectorizer] Potrace complete', 'color: #cc6600; font-weight: bold',
            { time: _vecMs + 'ms', paths: pathCount, svgSize: (svg.length / 1024).toFixed(1) + 'KB' });
          if (state.minDetailSize > 0) {
            svg = postProcessSvg(svg, state.minDetailSize);
          }
          callback(svg);
        });
        return;
      }

      console.error('%c[Vectorizer] No engine available', 'color: #ff0000; font-weight: bold');
      callback(null);
    }

    // Vectorize with scale factor (for full-res export where output SVG needs scaling)
    function vectorizeWithScale(imageData, svgScale, callback) {
      vectorizeImageData(imageData, {}, function(svg) {
        if (!svg || svgScale === 1) {
          callback(svg);
          return;
        }
        // Apply scale by wrapping paths in a transform group
        // Parse SVG, extract inner content, wrap in scaled group
        const match = svg.match(/<svg([^>]*)>([\s\S]*)<\/svg>/i);
        if (match) {
          const attrs = match[1];
          const inner = match[2];
          // Extract width/height from SVG attributes
          const wMatch = attrs.match(/width="([^"]+)"/);
          const hMatch = attrs.match(/height="([^"]+)"/);
          if (wMatch && hMatch) {
            const origW = parseFloat(wMatch[1]);
            const origH = parseFloat(hMatch[1]);
            const newW = origW * svgScale;
            const newH = origH * svgScale;
            svg = '<svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="' +
              newW + '" height="' + newH + '">' +
              '<g transform="scale(' + svgScale + ')">' + inner + '</g></svg>';
          }
        }
        callback(svg);
      });
    }

    function renderSvgToAdjustCanvas(svgString) {
      if (!svgString || !el.adjustCanvas) return;

      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = function() {
        const c = el.adjustCanvas;
        const targetCtx = ctx.adjust;
        targetCtx.save();
        targetCtx.setTransform(1, 0, 0, 1, 0, 0);
        targetCtx.clearRect(0, 0, c.width, c.height);
        // Fill white background
        targetCtx.fillStyle = '#ffffff';
        targetCtx.fillRect(0, 0, c.width, c.height);
        targetCtx.imageSmoothingEnabled = true;
        targetCtx.imageSmoothingQuality = 'high';
        // Letterbox: maintain aspect ratio (match processImage behavior)
        const cw = c.width, ch = c.height;
        const scale = Math.min(cw / img.naturalWidth, ch / img.naturalHeight);
        const dw = img.naturalWidth * scale;
        const dh = img.naturalHeight * scale;
        const dx = (cw - dw) / 2;
        const dy = (ch - dh) / 2;
        targetCtx.drawImage(img, dx, dy, dw, dh);
        targetCtx.restore();
        URL.revokeObjectURL(svgUrl);
      };
      img.onerror = function() {
        URL.revokeObjectURL(svgUrl);
      };
      img.src = svgUrl;
    }

    // ========== SVG Post-Processing ==========
    // Remove SVG paths smaller than minArea (by bounding box)
    function postProcessSvg(svgString, minArea) {
      if (!minArea || minArea <= 0) return svgString;

      // Parse SVG
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgString, 'image/svg+xml');
      const paths = doc.querySelectorAll('path');

      // To measure paths, we need them in a visible SVG context
      // Create a hidden SVG element temporarily
      const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      tempSvg.style.position = 'absolute';
      tempSvg.style.left = '-9999px';
      tempSvg.style.visibility = 'hidden';
      // Copy viewBox from original
      const origSvg = doc.querySelector('svg');
      if (origSvg && origSvg.getAttribute('viewBox')) {
        tempSvg.setAttribute('viewBox', origSvg.getAttribute('viewBox'));
      }
      document.body.appendChild(tempSvg);

      const toRemove = [];
      paths.forEach(path => {
        const clone = path.cloneNode(true);
        tempSvg.appendChild(clone);
        try {
          const bbox = clone.getBBox();
          const area = bbox.width * bbox.height;
          if (area < minArea) {
            toRemove.push(path);
          }
        } catch (e) {
          // getBBox can fail for empty paths
        }
        tempSvg.removeChild(clone);
      });

      document.body.removeChild(tempSvg);

      toRemove.forEach(p => p.remove());

      return new XMLSerializer().serializeToString(doc);
    }

    function analyzeAndRenderColors() {
      if (!state.originalImage) return;

      el.colorPalette.innerHTML = '<p class="color-loading">Analyzing colors...</p>';

      // Use setTimeout to allow UI to update
      setTimeout(() => {
        state.detectedColors = extractColors(state.originalImage, state.colorCount);
        renderColorPalette();
        processImageWithColors();
      }, 50);
    }
    
    function renderColorPalette() {
      if (!el.colorPalette) return;

      el.colorPalette.innerHTML = '';

      // Calculate total pixels for percentage display
      const totalPixels = state.detectedColors.reduce((sum, c) => sum + (c.count || 0), 0);

      state.detectedColors.forEach((color, idx) => {
        const swatch = document.createElement('div');
        swatch.className = 'color-swatch' + (color.selected ? '' : ' deselected');
        swatch.dataset.colorIdx = idx;

        const box = document.createElement('div');
        box.className = 'swatch-box';
        box.style.backgroundColor = color.hex;

        // Determine if checkmark should be dark or light
        const lum = 0.299 * color.r + 0.587 * color.g + 0.114 * color.b;
        if (lum > 128) {
          box.style.color = '#333';
          box.style.textShadow = 'none';
        }

        const label = document.createElement('span');
        label.className = 'swatch-label';
        const pct = totalPixels > 0 ? ((color.count / totalPixels) * 100).toFixed(1) : '0.0';
        label.textContent = `${color.hex.toUpperCase()} (${pct}%)`;

        // Percentage bar
        const pctBar = document.createElement('div');
        pctBar.className = 'swatch-pct';
        const pctFill = document.createElement('div');
        pctFill.className = 'swatch-pct-fill';
        pctFill.style.width = `${pct}%`;
        pctFill.style.backgroundColor = color.hex;
        pctBar.appendChild(pctFill);

        swatch.appendChild(box);
        swatch.appendChild(label);
        swatch.appendChild(pctBar);

        swatch.addEventListener('click', () => {
          color.selected = !color.selected;
          state.finalSvgString = null;
          swatch.classList.toggle('deselected', !color.selected);
          processImageWithColors();
          resetSvgPreview();
        });

        el.colorPalette.appendChild(swatch);
      });
    }
    
    // Color-aware processing that can target any canvas
    // useSliderAdjustments: false = Step 1 default behavior, true = Step 2 with slider values
    function processImageWithColorsOnCanvas(targetCtx, targetCanvas, useSliderAdjustments = false) {
      if (!state.originalImage || !state.colorModeEnabled) return;
      
      // Work in base size coordinates (context is scaled by DPR)
      const w = BASE_SIZE;
      const h = BASE_SIZE;
      
      // Clear canvas and fill with white background (prevents black letterbox bars)
      targetCtx.clearRect(0, 0, w, h);
      targetCtx.fillStyle = '#ffffff';
      targetCtx.fillRect(0, 0, w, h);
      
      const img = state.originalImage;
      const scale = Math.min(w / img.naturalWidth, h / img.naturalHeight);
      const dw = img.naturalWidth * scale;
      const dh = img.naturalHeight * scale;
      const dx = (w - dw) / 2;
      const dy = (h - dh) / 2;
      targetCtx.drawImage(img, dx, dy, dw, dh);
      
      // For pixel manipulation, work with actual canvas pixels
      targetCtx.save();
      targetCtx.setTransform(1, 0, 0, 1, 0, 0);
      
      const actualW = targetCanvas.width, actualH = targetCanvas.height;
      const imgData = targetCtx.getImageData(0, 0, actualW, actualH);
      const d = imgData.data;
      
      // Apply contrast enhancement only in Step 2 (when useSliderAdjustments is true)
      if (useSliderAdjustments) {
        const contrast = state.contrast;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] < 10) continue;
          for (let c = 0; c < 3; c++) {
            let val = d[i + c];
            val = ((val / 255 - 0.5) * contrast + 0.5) * 255;
            d[i + c] = Math.max(0, Math.min(255, val));
          }
        }
      }
      
      // Get selected colors
      const selectedColors = state.detectedColors.filter(c => c.selected);
      
      // Process each pixel
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] < 10) {
          d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; d[i + 3] = 255;
          continue;
        }
        
        const pixel = { r: d[i], g: d[i + 1], b: d[i + 2] };
        
        // White/near-white pixels are always background (not engraved)
        // This is semantically correct: white = no engraving
        const luminance = 0.299 * pixel.r + 0.587 * pixel.g + 0.114 * pixel.b;
        let isBlack;
        
        if (luminance > 240) {
          // White pixel = background, skip color matching
          isBlack = false;
        } else {
          // Dark/colored pixel = match to palette
          // Find the closest color among ALL detected colors, then check if it's selected
          const closest = findClosestColor(pixel, state.detectedColors);
          isBlack = closest && closest.selected;
          
          // In Step 2, apply additional tolerance filter if threshold is high
          // This allows users to exclude pixels that are "between" colors
          if (useSliderAdjustments && closest) {
            const dist = colorDistance(pixel, closest);
            // Threshold controls strictness: higher = only keep pixels very close to palette colors
            // Map threshold 0-255 to tolerance 300-50 (lower threshold = more permissive)
            const maxDistance = 300 - (state.threshold / 255) * 250;
            if (dist > maxDistance) {
              // Pixel is too far from any palette color - treat as background
              isBlack = false;
            }
          }
        }
        
        if (state.inverted) isBlack = !isBlack;
        
        if (isBlack) {
          d[i] = 0;
          d[i + 1] = 0;
          d[i + 2] = 0;
          d[i + 3] = 255;
        } else {
          d[i] = 255;
          d[i + 1] = 255;
          d[i + 2] = 255;
          d[i + 3] = 0;
        }
      }
      
      targetCtx.putImageData(imgData, 0, 0);
      targetCtx.restore();
      
      state.processedImageData = imgData;
      
      // Calculate and store the actual content bounds (non-white pixels)
      state.logoContentBounds = calculateContentBoundsFromImageData(imgData);
    }
    
    // Convenience wrapper for Step 1 converted preview canvas (uses default behavior)
    function processImageWithColors() {
      processImageWithColorsOnCanvas(ctx.converted, el.convertedPreview, false);
    }

    // ========== Text Rendering ==========
    // Store text bounds for hit detection
    let textBounds = {};
    let fontLoadPromise = null;
    
    // Load fonts before rendering text
    async function loadFontsForText() {
      const fontsToLoad = [];
      for (const [pos, data] of Object.entries(state.textLines)) {
        if (data.enabled && data.text.trim()) {
          const fontWeight = data.bold ? 'bold' : 'normal';
          const fontStyle = data.italic ? 'italic' : 'normal';
          const fontSize = parseInt(data.size, 10) || 22;
          const fontSpec = `${fontStyle} ${fontWeight} ${fontSize}px "${data.font}"`;
          fontsToLoad.push(fontSpec);
        }
      }
      
      if (fontsToLoad.length > 0 && document.fonts) {
        try {
          await Promise.all(fontsToLoad.map(f => document.fonts.load(f)));
        } catch (e) {
          // Font loading failed, continue anyway
          console.warn('Font loading failed:', e);
        }
      }
    }
    
    function renderTextPreview() {
      if (!ctx.textPreview) return;
      
      // Load fonts first, then render
      loadFontsForText().then(() => {
        renderTextPreviewImmediate();
      });
    }
    
    // Engraving boundary box (full 40mm x 40mm = 472x472px canvas)
    const ENGRAVE_BOUNDARY = {
      left: 0,
      top: 0,
      right: BASE_SIZE,
      bottom: BASE_SIZE,
      width: BASE_SIZE,
      height: BASE_SIZE
    };
    
    // Checkered pattern for transparency background
    let checkeredPattern = null;
    function createCheckeredPattern() {
      const patternCanvas = document.createElement('canvas');
      const patternSize = 16; // Size of each checker square
      patternCanvas.width = patternSize * 2;
      patternCanvas.height = patternSize * 2;
      const pCtx = patternCanvas.getContext('2d');
      
      // Light gray squares
      pCtx.fillStyle = '#f0f0f0';
      pCtx.fillRect(0, 0, patternSize * 2, patternSize * 2);
      
      // Dark gray squares (checkerboard pattern)
      pCtx.fillStyle = '#e0e0e0';
      pCtx.fillRect(0, 0, patternSize, patternSize);
      pCtx.fillRect(patternSize, patternSize, patternSize, patternSize);
      
      return patternCanvas;
    }
    
    // Store logo bounds for hit detection
    let logoBounds = null;
    
    function renderTextPreviewImmediate() {
      if (!ctx.textPreview) return;
      
      // Work in base size coordinates (context is scaled by DPR)
      const w = BASE_SIZE;
      const h = BASE_SIZE;

      // Clear text bounds and logo bounds
      textBounds = {};
      logoBounds = null;

      // Create checkered pattern if not already created
      if (!checkeredPattern) {
        checkeredPattern = createCheckeredPattern();
      }
      
      // Draw checkered background pattern
      ctx.textPreview.save();
      const pattern = ctx.textPreview.createPattern(checkeredPattern, 'repeat');
      ctx.textPreview.fillStyle = pattern;
      ctx.textPreview.fillRect(0, 0, w, h);
      ctx.textPreview.restore();
      
      // Draw dashed boundary border
      ctx.textPreview.save();
      ctx.textPreview.strokeStyle = '#888';
      ctx.textPreview.lineWidth = 2;
      ctx.textPreview.setLineDash([8, 4]);
      ctx.textPreview.strokeRect(1, 1, w - 2, h - 2);
      ctx.textPreview.restore();
      
      // Draw the logo/image with transform (position and scale)
      if (state.processedImageData && state.logoContentBounds) {
        const imgWidth = state.processedImageData.width;
        const imgHeight = state.processedImageData.height;
        const bounds = state.logoContentBounds;
        
        // Calculate scaled dimensions based on ACTUAL CONTENT, not full canvas
        const scale = state.imageTransform.scale;
        const contentWidth = bounds.width * scale;
        const contentHeight = bounds.height * scale;
        
        // Full image scaled dimensions (for drawing)
        const scaledWidth = imgWidth * scale;
        const scaledHeight = imgHeight * scale;
        
        // Offset from image top-left to content top-left (scaled)
        const contentOffsetX = bounds.minX * scale;
        const contentOffsetY = bounds.minY * scale;
        
        // Calculate desired CONTENT center position
        let contentCenterX = ENGRAVE_BOUNDARY.left + (ENGRAVE_BOUNDARY.width * state.imageTransform.x);
        let contentCenterY = ENGRAVE_BOUNDARY.top + (ENGRAVE_BOUNDARY.height * state.imageTransform.y);
        
        // Apply hard-stop constraints based on CONTENT dimensions (not full image)
        const halfContentWidth = contentWidth / 2;
        const halfContentHeight = contentHeight / 2;
        
        // Clamp content center so content edges stay within boundary
        const minCenterX = ENGRAVE_BOUNDARY.left + halfContentWidth;
        const maxCenterX = ENGRAVE_BOUNDARY.right - halfContentWidth;
        const minCenterY = ENGRAVE_BOUNDARY.top + halfContentHeight;
        const maxCenterY = ENGRAVE_BOUNDARY.bottom - halfContentHeight;
        
        // If content is larger than boundary, center it
        if (contentWidth >= ENGRAVE_BOUNDARY.width) {
          contentCenterX = ENGRAVE_BOUNDARY.left + (ENGRAVE_BOUNDARY.width / 2);
        } else {
          contentCenterX = Math.max(minCenterX, Math.min(maxCenterX, contentCenterX));
        }
        
        if (contentHeight >= ENGRAVE_BOUNDARY.height) {
          contentCenterY = ENGRAVE_BOUNDARY.top + (ENGRAVE_BOUNDARY.height / 2);
        } else {
          contentCenterY = Math.max(minCenterY, Math.min(maxCenterY, contentCenterY));
        }
        
        // Calculate where content top-left should be
        const contentDrawX = contentCenterX - halfContentWidth;
        const contentDrawY = contentCenterY - halfContentHeight;
        
        // Calculate where full image top-left should be (offset by content position within image)
        const drawX = contentDrawX - contentOffsetX;
        const drawY = contentDrawY - contentOffsetY;
        
        // Create a temporary canvas to scale the image data
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = imgWidth;
        tempCanvas.height = imgHeight;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.putImageData(state.processedImageData, 0, 0);
        
        // Draw scaled image
        ctx.textPreview.save();
        ctx.textPreview.drawImage(tempCanvas, drawX, drawY, scaledWidth, scaledHeight);
        ctx.textPreview.restore();
        
        // Store logo bounds for hit detection (use CONTENT bounds, not full image)
        logoBounds = {
          left: contentDrawX,
          top: contentDrawY,
          right: contentDrawX + contentWidth,
          bottom: contentDrawY + contentHeight,
          width: contentWidth,
          height: contentHeight,
          centerX: contentCenterX,
          centerY: contentCenterY
        };
      }

      // Draw text lines (these use the scaled context)
      ctx.textPreview.textAlign = 'left';
      ctx.textPreview.textBaseline = 'middle';

      // Max dimensions for text (full boundary width)
      const maxTextWidth = ENGRAVE_BOUNDARY.width - 10;  // Small padding from edges

      for (const [pos, data] of Object.entries(state.textLines)) {
        if (!data.enabled || !data.text.trim()) continue;

        const text = data.text.trim();
        const font = data.font;
        
        // Use numeric font size directly from setting (or default to 22)
        let fontSize = parseInt(data.size, 10) || 22;
        
        // Build font style string
        const fontStyle = data.italic ? 'italic ' : '';
        const fontWeight = data.bold ? 'bold ' : '';
        
        // Set font with the selected size
        ctx.textPreview.font = `${fontStyle}${fontWeight}${fontSize}px "${font}"`;
        
        // Reduce font size until text fits width constraint (if too long)
        while (ctx.textPreview.measureText(text).width > maxTextWidth && fontSize > 8) {
          fontSize--;
          ctx.textPreview.font = `${fontStyle}${fontWeight}${fontSize}px "${font}"`;
        }

        // Calculate text dimensions
        const metrics = ctx.textPreview.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize;
        const halfHeight = textHeight / 2;
        
        // Calculate desired position
        let textX = ENGRAVE_BOUNDARY.left + (ENGRAVE_BOUNDARY.width * data.x);
        let textY = ENGRAVE_BOUNDARY.top + (ENGRAVE_BOUNDARY.height * data.y);
        
        // Apply hard-stop constraints: text edges cannot exceed boundary
        // Left edge: textX >= boundary left
        // Right edge: textX + textWidth <= boundary right
        // Top edge: textY - halfHeight >= boundary top
        // Bottom edge: textY + halfHeight <= boundary bottom
        
        const minX = ENGRAVE_BOUNDARY.left;
        const maxX = ENGRAVE_BOUNDARY.right - textWidth;
        const minY = ENGRAVE_BOUNDARY.top + halfHeight;
        const maxY = ENGRAVE_BOUNDARY.bottom - halfHeight;
        
        textX = Math.max(minX, Math.min(maxX, textX));
        textY = Math.max(minY, Math.min(maxY, textY));
        
        // Store bounds for hit detection (left-aligned text)
        textBounds[pos] = {
          left: textX,
          right: textX + textWidth,
          top: textY - halfHeight,
          bottom: textY + halfHeight,
          width: textWidth,
          height: textHeight,
          centerX: textX + textWidth / 2,
          centerY: textY
        };

        // Draw selection highlight if this text is being dragged
        if (state.dragging.active && state.dragging.textLine === pos) {
          ctx.textPreview.save();
          ctx.textPreview.strokeStyle = '#0066cc';
          ctx.textPreview.lineWidth = 2;
          ctx.textPreview.setLineDash([5, 3]);
          ctx.textPreview.strokeRect(
            textBounds[pos].left - 4,
            textBounds[pos].top - 4,
            textWidth + 8,
            textHeight + 8
          );
          ctx.textPreview.restore();
        }

        // Draw the text
        ctx.textPreview.fillStyle = '#000';
        ctx.textPreview.fillText(text, textX, textY);
        
        // Draw underline if enabled (aligned with left-aligned text)
        if (data.underline) {
          const underlineY = textY + fontSize / 2 + 2;
          ctx.textPreview.beginPath();
          ctx.textPreview.strokeStyle = '#000';
          ctx.textPreview.lineWidth = Math.max(1, fontSize / 12);
          ctx.textPreview.moveTo(textX, underlineY);
          ctx.textPreview.lineTo(textX + textWidth, underlineY);
          ctx.textPreview.stroke();
        }
      }
    }

    // ========== Product Preview (SVG only) ==========
    async function renderProductSvgPreview() {
      if (!state.originalImage) return;
      
      // Generate SVG
      const svg = await generateSvg();
      if (!svg) return;
      
      // Crop and scale SVG so content fills consistently (touches parallel edges)
      const croppedSvg = cropAndScaleSvg(svg, el.textPreview, 40);
      
      // Get engraving color and opacity from config (theme settings)
      const engravingColor = config.engravingColor || '#888888';
      const engravingOpacity = (config.engravingOpacity || 60) / 100; // Convert percentage to decimal
      
      // Helper to set SVG content with frosted engraving effect
      const setSvgContent = (previewEl) => {
        if (!previewEl) return;
        previewEl.innerHTML = croppedSvg;
        
        // Apply theme settings via CSS custom properties
        previewEl.style.setProperty('--engraving-color', engravingColor);
        previewEl.style.setProperty('--engraving-opacity', engravingOpacity);
        
        const svgEl = previewEl.querySelector('svg');
        if (svgEl) {
          svgEl.removeAttribute('width');
          svgEl.removeAttribute('height');
          svgEl.style.width = '100%';
          svgEl.style.height = '100%';
        }
      };
      
      // Render SVG on both product previews
      setSvgContent(el.productSvgPreviewStandard);
      setSvgContent(el.productSvgPreviewHigh);
    }

    // ========== Final Preview (SVG only) ==========
    async function renderFinalSvgPreview() {
      if (!state.originalImage) return;
      
      // Generate SVG
      const svg = await generateSvg();
      if (!svg || !el.finalSvgPreview) return;
      
      // Crop and scale SVG so content fills consistently (touches parallel edges)
      const croppedSvg = cropAndScaleSvg(svg, el.textPreview, 40);
      
      el.finalSvgPreview.innerHTML = croppedSvg;
      
      const svgEl = el.finalSvgPreview.querySelector('svg');
      if (svgEl) {
        svgEl.removeAttribute('width');
        svgEl.removeAttribute('height');
        svgEl.style.width = '100%';
        svgEl.style.height = '100%';
      }
    }

    // ========== Pricing ==========
    function countTextLines() {
      let count = 0;
      for (const data of Object.values(state.textLines)) {
        if (data.enabled && data.text.trim()) count++;
      }
      return count;
    }

    function calculatePrice(product, qty) {
      // Base price × qty (qty is already number of glasses, but priced per 2)
      // Plus engraving fee per set of 2
      const basePrice = product === 'standard' 
        ? config.standardProofProduct.price 
        : config.highProofProduct.price;
      
      const sets = qty / 2; // Number of sets
      const glassTotal = (basePrice * qty); // Base glass cost
      const engravingTotal = sets * config.engravingFee * 100; // Engraving fee (in cents)
      
      return glassTotal + engravingTotal;
    }

    function updateOrderSummary() {
      const textLines = countTextLines();
      
      // Calculate total units (glasses) from quantities
      const totalUnits = state.quantities.standard + state.quantities.high;
      
      // Text cost: FREE in text-only mode, otherwise per line per unit
      const textCost = state.textOnlyMode ? 0 : (textLines * config.textLineFee * totalUnits);

      let total = 0;

      // Standard Proof - always visible
      el.qtyStandard.textContent = state.quantities.standard;
      const standardPrice = calculatePrice('standard', state.quantities.standard);
      el.priceStandard.textContent = formatMoney(standardPrice);
      total += standardPrice;

      // High Proof - always visible
      el.qtyHigh.textContent = state.quantities.high;
      const highPrice = calculatePrice('high', state.quantities.high);
      el.priceHigh.textContent = formatMoney(highPrice);
      total += highPrice;

      // Text lines - show info but no charge in text-only mode
      if (textLines > 0 && totalUnits > 0) {
        el.orderText.style.display = '';
        if (state.textOnlyMode) {
          el.textLinesSummary.textContent = `${textLines} line${textLines > 1 ? 's' : ''} (included)`;
          el.priceText.textContent = 'Included';
        } else {
          const feeInDollars = (config.textLineFee / 100).toFixed(2);
          el.textLinesSummary.textContent = `${textLines} line${textLines > 1 ? 's' : ''} × $${feeInDollars} × ${totalUnits} units`;
          el.priceText.textContent = formatMoney(textCost);
          total += textCost;
        }
      } else {
        el.orderText.style.display = 'none';
      }

      el.orderTotal.textContent = formatMoney(total);
    }

    function formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    // ========== Add to Cart ==========
    async function addToCart() {
      const totalQty = state.quantities.standard + state.quantities.high;
      if (totalQty === 0) {
        setCartStatus('Please add at least one product to your order', 'error');
        return;
      }

      // Validate variant IDs before attempting to add
      const standardVariantId = config.standardProofProduct?.variantId;
      const highVariantId = config.highProofProduct?.variantId;
      
      if (state.quantities.standard > 0 && (!standardVariantId || standardVariantId === 0 || standardVariantId === '0')) {
        setCartStatus('Standard Proof product not configured. Please contact support.', 'error');
        console.error('Missing Standard Proof variant ID:', standardVariantId);
        return;
      }
      
      if (state.quantities.high > 0 && (!highVariantId || highVariantId === 0 || highVariantId === '0')) {
        setCartStatus('High Proof product not configured. Please contact support.', 'error');
        console.error('Missing High Proof variant ID:', highVariantId);
        return;
      }

      el.addToCartBtn.disabled = true;
      setCartStatus('Processing...');

      try {
        // Generate SVG using Potrace
        let svgString = null;
        if (window.Potrace) {
          try {
            svgString = await generateSvg();
          } catch (svgErr) {
            console.warn('SVG generation failed:', svgErr);
          }
        }

        // Crop/scale SVG to 40mm × 40mm square with content touching edges
        let croppedSvgString = null;
        if (svgString && el.textPreview) {
          croppedSvgString = cropAndScaleSvg(svgString, el.textPreview, 40);
        }

        // Generate PNG at 300 DPI (472×472) by rendering the cropped SVG
        // Embed DPI metadata so it opens at correct size in Illustrator/Photoshop
        let pngBlob = null;
        if (croppedSvgString) {
          try {
            const pngCanvas = await renderSvgToPngCanvas(svgString, EXPORT_SIZE_300DPI);
            if (pngCanvas) {
              const rawBlob = await new Promise(resolve => {
                pngCanvas.toBlob(resolve, 'image/png', 1.0);
              });
              // Embed 300 DPI metadata into the PNG
              pngBlob = await embedPngDpi(rawBlob, 300);
            }
          } catch (pngErr) {
            console.warn('PNG generation failed:', pngErr);
          }
        }

        // Upload both SVG and PNG to AWS if endpoint configured
        let svgUrl = null;
        let pngUrl = null;
        
        if (config.awsEndpoint) {
          // Upload cropped SVG
          if (croppedSvgString) {
            try {
              const svgBlob = new Blob([croppedSvgString], { type: 'image/svg+xml' });
              svgUrl = await uploadToAws(svgBlob, 'image/svg+xml', 'svg');
            } catch (uploadErr) {
              console.warn('SVG upload failed:', uploadErr);
            }
          }
          
          // Upload PNG
          if (pngBlob) {
            try {
              pngUrl = await uploadToAws(pngBlob, 'image/png', 'png');
            } catch (uploadErr) {
              console.warn('PNG upload failed:', uploadErr);
            }
          }
        }

        state.finalDesignBlob = pngBlob;
        state.finalSvgString = svgString;

        // Build cart items
        const items = [];
        const textLines = countTextLines();
        const textDetails = [];
        
        for (const [pos, data] of Object.entries(state.textLines)) {
          if (data.enabled && data.text.trim()) {
            textDetails.push(`${pos}: "${data.text}" (${data.font})`);
          }
        }

        const properties = {
          '_Engraving SVG (40mm)': svgUrl || 'Not available',
          '_Engraving PNG (300dpi)': pngUrl || 'Not available',
          '_Text Lines': textLines > 0 ? textDetails.join(', ') : 'None',
          '_Text Line Count': String(textLines),
          '_Engraving Type': state.textOnlyMode ? 'Text Only' : 'Logo + Text',
          '_Threshold': state.textOnlyMode ? 'N/A' : String(state.threshold),
          '_Inverted': state.textOnlyMode ? 'N/A' : (state.inverted ? 'Yes' : 'No')
        };

        if (state.quantities.standard > 0) {
          const standardTextFeeCents = state.textOnlyMode ? 0 : (textLines * config.textLineFee * state.quantities.standard);
          const feeInDollars = (config.textLineFee / 100).toFixed(2);
          items.push({
            id: standardVariantId,
            quantity: state.quantities.standard,
            properties: {
              ...properties,
              '_Engraving Fee': state.textOnlyMode ? 'Included' : `$${(state.quantities.standard / 2 * config.engravingFee).toFixed(2)}`,
              '_Text Fee': state.textOnlyMode ? 'Included (Text Only)' : (textLines > 0 ? `$${(standardTextFeeCents / 100).toFixed(2)} (${textLines} line${textLines > 1 ? 's' : ''} × $${feeInDollars} × ${state.quantities.standard} units)` : '$0.00')
            }
          });
        }

        if (state.quantities.high > 0) {
          const highTextFeeCents = state.textOnlyMode ? 0 : (textLines * config.textLineFee * state.quantities.high);
          const feeInDollars = (config.textLineFee / 100).toFixed(2);
          items.push({
            id: highVariantId,
            quantity: state.quantities.high,
            properties: {
              ...properties,
              '_Engraving Fee': state.textOnlyMode ? 'Included' : `$${(state.quantities.high / 2 * config.engravingFee).toFixed(2)}`,
              '_Text Fee': state.textOnlyMode ? 'Included (Text Only)' : (textLines > 0 ? `$${(highTextFeeCents / 100).toFixed(2)} (${textLines} line${textLines > 1 ? 's' : ''} × $${feeInDollars} × ${state.quantities.high} units)` : '$0.00')
            }
          });
        }

        console.log('Adding to cart:', items);

        // Add to cart via Shopify AJAX API
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Cart API error:', response.status, errorData);
          throw new Error(errorData.description || 'Failed to add to cart');
        }

        setCartStatus('Added to cart successfully!', 'success');
        
        // Redirect to cart after delay
        setTimeout(() => {
          window.location.href = '/cart';
        }, 1500);

      } catch (err) {
        console.error('Add to cart error:', err);
        setCartStatus(err.message || 'Failed to add to cart. Please try again.', 'error');
        el.addToCartBtn.disabled = false;
      }
    }

    async function uploadToAws(blob, contentType = 'image/svg+xml', extension = 'svg') {
      const filename = `${state.originalFileName || 'design'}-engraving.${extension}`;
      
      const presignRes = await fetch(config.awsEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          contentType
        })
      });

      if (!presignRes.ok) throw new Error('Presign failed');
      const data = await presignRes.json();

      const putRes = await fetch(data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: blob
      });

      if (!putRes.ok) throw new Error('Upload failed');
      return data.fileUrl;
    }

    // Generate full-resolution logo SVG without any user scaling
    // This preserves maximum detail - scaling is applied via SVG transforms later
    async function generateFullResLogoSvg() {
      return new Promise((resolve) => {
        if (!window.Potrace || !state.processedImageData || !state.logoContentBounds) {
          resolve(null);
          return;
        }

        const bounds = state.logoContentBounds;
        const scale = 4; // 4x upscale for cleaner SVG curves
        
        // Canvas sized to just the content bounds (not the full engraving area)
        const contentWidth = bounds.width;
        const contentHeight = bounds.height;
        const w = contentWidth * scale;
        const h = contentHeight * scale;
        
        const hiResCanvas = document.createElement('canvas');
        hiResCanvas.width = w;
        hiResCanvas.height = h;
        const hiResCtx = hiResCanvas.getContext('2d');
        
        // Fill with white background
        hiResCtx.fillStyle = '#ffffff';
        hiResCtx.fillRect(0, 0, w, h);
        
        hiResCtx.imageSmoothingEnabled = true;
        hiResCtx.imageSmoothingQuality = 'high';
        
        // Create temp canvas from processedImageData
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = state.processedImageData.width;
        tempCanvas.height = state.processedImageData.height;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.putImageData(state.processedImageData, 0, 0);
        
        // Draw just the content region, scaled up for high-quality vectorization
        // Source: content bounds from processedImageData
        // Dest: full hiRes canvas
        hiResCtx.drawImage(
          tempCanvas,
          bounds.minX, bounds.minY, bounds.width, bounds.height,  // source rect
          0, 0, w, h  // dest rect (full canvas)
        );
        
        // Apply selective gap fill and edge smoothing for cleaner vectorization
        const imgData = hiResCtx.getImageData(0, 0, w, h);
        if (state.fillGaps) {
          applySelectiveGapFill(imgData, w, h, state.gapFillSize);
        }
        if (state.antiAliasing) {
          applySmoothEdges(imgData, w, h);
        }
        hiResCtx.putImageData(imgData, 0, 0);

        const finalImgData = hiResCtx.getImageData(0, 0, w, h);
        const svgScale = 1 / scale;

        vectorizeWithScale(finalImgData, svgScale, function(svg) {
          state.fullResLogoDimensions = {
            width: contentWidth,
            height: contentHeight
          };
          state.fullResLogoSvg = svg;
          resolve(svg);
        });
      });
    }

    async function generateSvg() {
      // TEXT-ONLY MODE: Generate SVG with just text, no image
      if (state.textOnlyMode) {
        if (!hasTextEnabled()) {
          return null;
        }
        
        // Create an SVG with just the text elements
        let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${BASE_SIZE} ${BASE_SIZE}" width="${BASE_SIZE}" height="${BASE_SIZE}">`;
        svg = addTextToSvg(svg, BASE_SIZE);
        
        state.finalSvgString = svg;
        return svg;
      }
      
      if (!window.Potrace || !state.originalImage) {
        return null;
      }

      // NEW STRATEGY: Vectorize logo at full resolution, then apply scale/position via SVG transforms
      // This preserves maximum detail regardless of user's scale setting
      
      // Generate or use cached full-res logo SVG
      if (!state.fullResLogoSvg && state.processedImageData && state.logoContentBounds) {
        await generateFullResLogoSvg();
      }
      
      // If we have a full-res logo SVG, use the transform-based approach
      if (state.fullResLogoSvg && state.fullResLogoDimensions) {
        const svg = buildSvgWithTransforms();
        state.finalSvgString = svg;
        return svg;
      }
      
      // Fallback to original raster-based approach if no processedImageData
      return new Promise((resolve) => {
        const scale = 4;
        const img = state.originalImage;
        const w = BASE_SIZE * scale;
        const h = BASE_SIZE * scale;
        
        const hiResCanvas = document.createElement('canvas');
        hiResCanvas.width = w;
        hiResCanvas.height = h;
        const hiResCtx = hiResCanvas.getContext('2d');
        
        hiResCtx.fillStyle = '#ffffff';
        hiResCtx.fillRect(0, 0, w, h);
        hiResCtx.imageSmoothingEnabled = true;
        hiResCtx.imageSmoothingQuality = 'high';
        
        const baseImgScale = Math.min(w / img.naturalWidth, h / img.naturalHeight);
        const userScale = state.imageTransform.scale;
        const finalImgScale = baseImgScale * userScale;
        
        const dw = img.naturalWidth * finalImgScale;
        const dh = img.naturalHeight * finalImgScale;
        
        const centerX = w * state.imageTransform.x;
        const centerY = h * state.imageTransform.y;
        const dx = centerX - (dw / 2);
        const dy = centerY - (dh / 2);
        
        hiResCtx.drawImage(img, dx, dy, dw, dh);
        
        const imgData = hiResCtx.getImageData(0, 0, w, h);
        const d = imgData.data;
        const threshold = state.threshold;
        const edgeSoftness = 30;
        
        for (let i = 0; i < d.length; i += 4) {
          if (d[i + 3] < 10) {
            d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; d[i + 3] = 255;
            continue;
          }
          const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          const diff = lum - threshold;
          const sigmoid = 1 / (1 + Math.exp(-diff / edgeSoftness));
          const outputVal = sigmoid * 255;
          d[i] = outputVal; d[i + 1] = outputVal; d[i + 2] = outputVal; d[i + 3] = 255;
        }
        
        hiResCtx.putImageData(imgData, 0, 0);

        const finalImgData = hiResCtx.getImageData(0, 0, w, h);
        const svgScale = BASE_SIZE / w;

        vectorizeWithScale(finalImgData, svgScale, function(svg) {
          if (hasTextEnabled()) {
            svg = addTextToSvg(svg, BASE_SIZE);
          }
          state.finalSvgString = svg;
          resolve(svg);
        });
      });
    }
    
    // Build final SVG using full-res logo paths with SVG transforms for scale/position
    function buildSvgWithTransforms() {
      const bounds = state.logoContentBounds;
      const dims = state.fullResLogoDimensions;
      const userScale = state.imageTransform.scale;
      
      // Calculate scaled content dimensions
      const contentWidth = dims.width * userScale;
      const contentHeight = dims.height * userScale;
      
      // Calculate content center position with boundary constraints (same as canvas)
      let contentCenterX = BASE_SIZE * state.imageTransform.x;
      let contentCenterY = BASE_SIZE * state.imageTransform.y;
      
      const halfContentWidth = contentWidth / 2;
      const halfContentHeight = contentHeight / 2;
      
      const minCenterX = halfContentWidth;
      const maxCenterX = BASE_SIZE - halfContentWidth;
      const minCenterY = halfContentHeight;
      const maxCenterY = BASE_SIZE - halfContentHeight;
      
      if (contentWidth >= BASE_SIZE) {
        contentCenterX = BASE_SIZE / 2;
      } else {
        contentCenterX = Math.max(minCenterX, Math.min(maxCenterX, contentCenterX));
      }
      
      if (contentHeight >= BASE_SIZE) {
        contentCenterY = BASE_SIZE / 2;
      } else {
        contentCenterY = Math.max(minCenterY, Math.min(maxCenterY, contentCenterY));
      }
      
      // Calculate top-left position for the scaled content
      const contentX = contentCenterX - halfContentWidth;
      const contentY = contentCenterY - halfContentHeight;
      
      // Extract paths from the full-res logo SVG
      const parser = new DOMParser();
      const logoDoc = parser.parseFromString(state.fullResLogoSvg, 'image/svg+xml');
      const logoSvgEl = logoDoc.documentElement;
      
      // Get all path elements from the logo SVG
      const paths = logoSvgEl.querySelectorAll('path');
      let pathsString = '';
      paths.forEach(path => {
        pathsString += path.outerHTML;
      });
      
      // Build final SVG with transform group
      // Transform: translate to position, then scale
      let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${BASE_SIZE} ${BASE_SIZE}" width="${BASE_SIZE}" height="${BASE_SIZE}">`;
      svg += `<g transform="translate(${contentX}, ${contentY}) scale(${userScale})">`;
      svg += pathsString;
      svg += '</g>';
      svg += '</svg>';
      
      // Add text as vector SVG elements
      if (hasTextEnabled()) {
        svg = addTextToSvg(svg, BASE_SIZE);
      }
      
      return svg;
    }
    
    // Add text as vector SVG elements
    function addTextToSvg(svgString, size) {
      // Parse the SVG to insert text elements
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgString, 'image/svg+xml');
      const svgEl = doc.documentElement;
      
      // Ensure viewBox is set
      if (!svgEl.getAttribute('viewBox')) {
        svgEl.setAttribute('viewBox', `0 0 ${size} ${size}`);
      }
      
      const maxTextWidth = size - 10; // Match canvas: ENGRAVE_BOUNDARY.width - 10
      
      for (const [pos, data] of Object.entries(state.textLines)) {
        if (!data.enabled || !data.text.trim()) continue;
        
        const text = data.text.trim();
        const font = data.font;
        
        // Use numeric font size directly from setting (or default to 22)
        let fontSize = parseInt(data.size, 10) || 22;
        
        // Reduce font size if text is too long for the width (match canvas logic)
        let estimatedCharWidth = fontSize * 0.6;
        while (text.length * estimatedCharWidth > maxTextWidth && fontSize > 8) {
          fontSize--;
          estimatedCharWidth = fontSize * 0.6;
        }
        
        // Calculate text dimensions for clamping
        const textWidth = text.length * estimatedCharWidth;
        const textHeight = fontSize;
        const halfHeight = textHeight / 2;
        
        // Calculate initial position from state
        let textX = size * data.x;
        let textY = size * data.y;
        
        // Apply hard-stop constraints (same as canvas version)
        // Left edge: textX >= 0
        // Right edge: textX + textWidth <= size
        // Top edge: textY - halfHeight >= 0
        // Bottom edge: textY + halfHeight <= size
        const minX = 0;
        const maxX = size - textWidth;
        const minY = halfHeight;
        const maxY = size - halfHeight;
        
        textX = Math.max(minX, Math.min(maxX, textX));
        textY = Math.max(minY, Math.min(maxY, textY));
        
        // Create text element
        const textEl = doc.createElementNS('http://www.w3.org/2000/svg', 'text');
        textEl.setAttribute('x', textX);
        textEl.setAttribute('y', textY);
        textEl.setAttribute('text-anchor', 'start');
        textEl.setAttribute('dominant-baseline', 'middle');
        textEl.setAttribute('fill', 'black');
        textEl.setAttribute('font-family', `"${font}", sans-serif`);
        textEl.setAttribute('font-size', fontSize);
        
        // Apply bold
        if (data.bold) {
          textEl.setAttribute('font-weight', 'bold');
        }
        
        // Apply italic
        if (data.italic) {
          textEl.setAttribute('font-style', 'italic');
        }
        
        // Apply underline using text-decoration
        if (data.underline) {
          textEl.setAttribute('text-decoration', 'underline');
        }
        
        textEl.textContent = text;
        svgEl.appendChild(textEl);
      }
      
      // Serialize back to string
      const serializer = new XMLSerializer();
      return serializer.serializeToString(doc);
    }
    
    // Helper function to check if any text lines are enabled
    function hasTextEnabled() {
      for (const data of Object.values(state.textLines)) {
        if (data.enabled && data.text.trim()) return true;
      }
      return false;
    }

    function setCartStatus(msg, type = '') {
      if (el.cartStatus) {
        el.cartStatus.textContent = msg;
        el.cartStatus.className = 'cart-status ' + type;
      }
    }

    // ========== Download Functions ==========
    async function downloadPng() {
      // Generate SVG first - PNG will be rendered from the cropped/scaled SVG
      let svg = state.finalSvgString;
      if (!svg && window.Potrace) {
        svg = await generateSvg();
      }
      
      if (!svg) {
        // Fallback to canvas if SVG generation fails
        alert('PNG download requires SVG generation. Please try again.');
        return;
      }
      
      // Render the cropped/scaled SVG to PNG at 300 DPI (472x472)
      const pngCanvas = await renderSvgToPngCanvas(svg, EXPORT_SIZE_300DPI);
      
      if (!pngCanvas) {
        alert('Failed to generate PNG. Please try again.');
        return;
      }
      
      // Convert canvas to blob, then embed 300 DPI metadata
      const rawBlob = await new Promise(resolve => pngCanvas.toBlob(resolve, 'image/png', 1.0));
      const pngWithDpi = await embedPngDpi(rawBlob, 300);
      
      const link = document.createElement('a');
      link.download = `${state.originalFileName}-engraving.png`;
      link.href = URL.createObjectURL(pngWithDpi);
      link.click();
      URL.revokeObjectURL(link.href);
    }

    // Get bounding box of non-white content in a canvas
    function getContentBounds(canvas) {
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      let minX = canvas.width, minY = canvas.height;
      let maxX = 0, maxY = 0;
      let hasContent = false;
      
      // Strict threshold: 254 means only pure white (255,255,255) is ignored
      const WHITE_THRESHOLD = 254;
      
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const i = (y * canvas.width + x) * 4;
          // Check if pixel is non-white (has content)
          if (data[i] < WHITE_THRESHOLD || data[i+1] < WHITE_THRESHOLD || data[i+2] < WHITE_THRESHOLD) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
            hasContent = true;
          }
        }
      }
      
      if (!hasContent) {
        // No content found, return full canvas
        console.log('[DEBUG] getContentBounds: No content found, using full canvas');
        return { minX: 0, minY: 0, maxX: canvas.width - 1, maxY: canvas.height - 1, 
                 width: canvas.width, height: canvas.height };
      }
      
      const bounds = { 
        minX, minY, maxX, maxY, 
        width: maxX - minX + 1, 
        height: maxY - minY + 1 
      };
      
      // DEBUG: Log bounds detection
      console.log('[DEBUG] getContentBounds:', {
        canvasSize: { w: canvas.width, h: canvas.height },
        bounds: bounds,
        contentRatio: (bounds.width / bounds.height).toFixed(3),
        margins: {
          left: minX,
          right: canvas.width - maxX - 1,
          top: minY,
          bottom: canvas.height - maxY - 1
        }
      });
      
      return bounds;
    }

    // Export size: 472px = 300 DPI at 40mm (40mm = 1.575in, 1.575 × 300 = 472)
    const EXPORT_SIZE_300DPI = 472;

    // Embed DPI metadata (pHYs chunk) into a PNG blob
    // This makes the PNG open at the correct physical size in Illustrator/Photoshop
    async function embedPngDpi(blob, dpi = 300) {
      const arrayBuffer = await blob.arrayBuffer();
      const data = new Uint8Array(arrayBuffer);
      
      // PNG signature is 8 bytes, then chunks follow
      // We need to insert pHYs chunk before the first IDAT chunk
      
      // Calculate pixels per meter (DPI × 39.3701)
      const pixelsPerMeter = Math.round(dpi * 39.3701);
      
      // Create pHYs chunk (9 bytes of data)
      // Format: 4 bytes X pixels/unit, 4 bytes Y pixels/unit, 1 byte unit (1=meter)
      const phys = new Uint8Array(21); // 4 length + 4 type + 9 data + 4 crc
      const physView = new DataView(phys.buffer);
      
      // Length (9 bytes of data)
      physView.setUint32(0, 9, false); // big endian
      
      // Chunk type "pHYs"
      phys[4] = 0x70; // p
      phys[5] = 0x48; // H
      phys[6] = 0x59; // Y
      phys[7] = 0x73; // s
      
      // X pixels per unit (big endian)
      physView.setUint32(8, pixelsPerMeter, false);
      
      // Y pixels per unit (big endian)
      physView.setUint32(12, pixelsPerMeter, false);
      
      // Unit specifier (1 = meter)
      phys[16] = 1;
      
      // Calculate CRC32 of type + data (bytes 4-16)
      const crc = crc32(phys.slice(4, 17));
      physView.setUint32(17, crc, false);
      
      // Find position to insert (after IHDR chunk, before IDAT)
      // PNG signature (8) + IHDR length (4) + IHDR type (4) + IHDR data (13) + IHDR crc (4) = 33
      const insertPos = 33;
      
      // Create new array with pHYs chunk inserted
      const newData = new Uint8Array(data.length + 21);
      newData.set(data.slice(0, insertPos), 0);
      newData.set(phys, insertPos);
      newData.set(data.slice(insertPos), insertPos + 21);
      
      return new Blob([newData], { type: 'image/png' });
    }
    
    // CRC32 calculation for PNG chunks
    function crc32(data) {
      let crc = 0xFFFFFFFF;
      const table = getCrc32Table();
      
      for (let i = 0; i < data.length; i++) {
        crc = (crc >>> 8) ^ table[(crc ^ data[i]) & 0xFF];
      }
      
      return (crc ^ 0xFFFFFFFF) >>> 0;
    }
    
    // Lazy-initialized CRC32 lookup table
    let crc32Table = null;
    function getCrc32Table() {
      if (crc32Table) return crc32Table;
      
      crc32Table = new Uint32Array(256);
      for (let i = 0; i < 256; i++) {
        let c = i;
        for (let j = 0; j < 8; j++) {
          c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        }
        crc32Table[i] = c;
      }
      return crc32Table;
    }

    // Render SVG string to a PNG canvas at specified size
    // This ensures PNG matches the SVG exactly
    function renderSvgToPngCanvas(svgString, targetSize = EXPORT_SIZE_300DPI) {
      return new Promise((resolve) => {
        // First crop/scale the SVG to 40mm square with content touching edges
        const croppedSvg = cropAndScaleSvg(svgString, el.textPreview, 40);
        
        // Create a blob URL from the cropped SVG
        const svgBlob = new Blob([croppedSvg], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        
        const img = new Image();
        img.onload = function() {
          // Create canvas at target resolution
          const canvas = document.createElement('canvas');
          canvas.width = targetSize;
          canvas.height = targetSize;
          const ctx = canvas.getContext('2d');
          
          // Fill with white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, targetSize, targetSize);
          
          // Draw SVG at target resolution with high quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, targetSize, targetSize);
          
          URL.revokeObjectURL(svgUrl);
          resolve(canvas);
        };
        
        img.onerror = function() {
          URL.revokeObjectURL(svgUrl);
          console.warn('Failed to render SVG to canvas');
          resolve(null);
        };
        
        img.src = svgUrl;
      });
    }

    // Create a high-resolution PNG canvas (square) with content scaled to fill
    // Content touches at least one edge while maintaining aspect ratio, centered in square
    function createScaledPngCanvas(sourceCanvas, targetSize = EXPORT_SIZE_HIRES) {
      const bounds = getContentBounds(sourceCanvas);
      
      // Calculate scale to fit content into target size (maintain aspect ratio)
      // Content will touch at least one edge
      const scaleX = targetSize / bounds.width;
      const scaleY = targetSize / bounds.height;
      const scale = Math.min(scaleX, scaleY);
      
      // Calculate final dimensions - content touches one edge
      const scaledWidth = Math.round(bounds.width * scale);
      const scaledHeight = Math.round(bounds.height * scale);
      
      // Create output canvas at exact target size (always square)
      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = targetSize;
      outputCanvas.height = targetSize;
      const outputCtx = outputCanvas.getContext('2d');
      
      // Fill with white background
      outputCtx.fillStyle = '#ffffff';
      outputCtx.fillRect(0, 0, targetSize, targetSize);
      
      // Center the scaled content in the square canvas
      const offsetX = Math.round((targetSize - scaledWidth) / 2);
      const offsetY = Math.round((targetSize - scaledHeight) / 2);
      
      // DEBUG: Log scaling calculations
      console.log('[DEBUG] createScaledPngCanvas:', {
        targetSize,
        bounds: { w: bounds.width, h: bounds.height },
        scales: { scaleX: scaleX.toFixed(4), scaleY: scaleY.toFixed(4), chosen: scale.toFixed(4) },
        scaled: { w: scaledWidth, h: scaledHeight },
        offsets: { x: offsetX, y: offsetY },
        touchesEdge: {
          horizontal: scaledWidth === targetSize,
          vertical: scaledHeight === targetSize
        }
      });
      
      // Draw cropped and scaled content with high-quality rendering
      outputCtx.imageSmoothingEnabled = true;
      outputCtx.imageSmoothingQuality = 'high';
      outputCtx.drawImage(
        sourceCanvas,
        bounds.minX, bounds.minY, bounds.width, bounds.height,  // Source: crop to content bounds
        offsetX, offsetY, scaledWidth, scaledHeight              // Dest: scaled and centered
      );
      
      return outputCanvas;
    }

    // Get the actual bounding box of SVG content by rendering to DOM
    // This gives us the true bounds of the vector paths, not the canvas pixels
    function getSvgContentBounds(svgString) {
      // Create a temporary container to render the SVG
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.visibility = 'hidden';
      container.style.left = '-9999px';
      container.innerHTML = svgString;
      document.body.appendChild(container);
      
      const svgEl = container.querySelector('svg');
      let bounds = null;
      
      if (svgEl) {
        try {
          // Get the bounding box of all SVG content
          const bbox = svgEl.getBBox();
          bounds = {
            minX: bbox.x,
            minY: bbox.y,
            maxX: bbox.x + bbox.width,
            maxY: bbox.y + bbox.height,
            width: bbox.width,
            height: bbox.height
          };
        } catch (e) {
          console.warn('Could not get SVG bounding box:', e);
        }
      }
      
      // Clean up
      document.body.removeChild(container);
      
      return bounds;
    }

    // Crop and scale SVG to content bounds, output as 40mm × 40mm square
    // Content is scaled to touch at least one edge, centered in the square
    function cropAndScaleSvg(svgString, sourceCanvas, targetMm = 40) {
      // Get the actual SVG content bounds (more accurate than canvas bounds)
      const svgBounds = getSvgContentBounds(svgString);
      
      // Fallback to canvas bounds if SVG bounds detection fails
      const bounds = svgBounds || getContentBounds(sourceCanvas);
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgString, 'image/svg+xml');
      const svgEl = doc.documentElement;
      
      // Get the original SVG viewBox to understand its coordinate system
      const originalViewBox = svgEl.getAttribute('viewBox');
      
      // The viewBox will be a square that contains the content, centered
      // maxDimension determines the square size so content touches at least one edge
      const maxDimension = Math.max(bounds.width, bounds.height);
      const squareSize = maxDimension;
      
      // Calculate offsets to center content in the square viewBox
      const contentOffsetX = (squareSize - bounds.width) / 2;
      const contentOffsetY = (squareSize - bounds.height) / 2;
      
      // Set viewBox as a square, with content area positioned inside
      // viewBox origin is adjusted so content appears centered
      const viewBoxX = bounds.minX - contentOffsetX;
      const viewBoxY = bounds.minY - contentOffsetY;
      
      // DEBUG: Log SVG cropping calculations
      console.log('[DEBUG] cropAndScaleSvg:', {
        usingSvgBounds: !!svgBounds,
        originalViewBox,
        bounds: { minX: bounds.minX, minY: bounds.minY, w: bounds.width, h: bounds.height },
        squareSize,
        offsets: { contentOffsetX, contentOffsetY },
        newViewBox: { x: viewBoxX, y: viewBoxY, size: squareSize },
        outputSize: `${targetMm}mm x ${targetMm}mm`
      });
      
      svgEl.setAttribute('viewBox', `${viewBoxX} ${viewBoxY} ${squareSize} ${squareSize}`);
      
      // Always output as 40mm × 40mm square
      svgEl.setAttribute('width', `${targetMm}mm`);
      svgEl.setAttribute('height', `${targetMm}mm`);
      
      const serializer = new XMLSerializer();
      return serializer.serializeToString(doc);
    }

    // Set SVG dimensions to 40mm × 40mm for laser engraving (legacy - use cropAndScaleSvg instead)
    function setSvgDimensions(svgString, widthMm = 40, heightMm = 40) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgString, 'image/svg+xml');
      const svgEl = doc.documentElement;
      
      // Ensure viewBox is set
      if (!svgEl.getAttribute('viewBox')) {
        svgEl.setAttribute('viewBox', `0 0 ${BASE_SIZE} ${BASE_SIZE}`);
      }
      
      // Set physical dimensions
      svgEl.setAttribute('width', `${widthMm}mm`);
      svgEl.setAttribute('height', `${heightMm}mm`);
      
      const serializer = new XMLSerializer();
      return serializer.serializeToString(doc);
    }

    async function downloadSvg() {
      let svg = state.finalSvgString;
      if (!svg && window.Potrace) {
        svg = await generateSvg();
      }
      
      if (!svg && window.ImageTracer) {
        // Fallback to ImageTracer if available
        svg = await new Promise((resolve) => {
          const dataUrl = el.textPreview.toDataURL('image/png');
          window.ImageTracer.imageToSVG(dataUrl, (svgStr) => resolve(svgStr), {
            numberofcolors: 2,
            colorquantcycles: 1,
            blurradius: 0,
            ltres: 1,
            qtres: 1,
            pathomit: 8
          });
        });
      }
      
      if (!svg) {
        alert('SVG download is not available. Please use PNG download instead.');
        return;
      }

      // Crop to content bounds and set to 40mm (longest edge)
      svg = cropAndScaleSvg(svg, el.textPreview, 40);

      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const link = document.createElement('a');
      link.download = `${state.originalFileName}-engraving.svg`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
    }

    async function downloadBoth() {
      await downloadPng();
      await downloadSvg();
    }

    // ========== Text Dragging ==========
    function getCanvasCoords(e, canvas) {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      
      // Convert to canvas coordinates (accounting for CSS scaling)
      const scaleX = BASE_SIZE / rect.width;
      const scaleY = BASE_SIZE / rect.height;
      
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY
      };
    }
    
    function hitTestText(x, y) {
      // Check if coordinates hit any text bounds
      for (const [pos, bounds] of Object.entries(textBounds)) {
        if (x >= bounds.left && x <= bounds.right &&
            y >= bounds.top && y <= bounds.bottom) {
          return pos;
        }
      }
      return null;
    }
    
    function hitTestLogo(x, y) {
      // Check if coordinates hit the logo bounds
      if (logoBounds) {
        if (x >= logoBounds.left && x <= logoBounds.right &&
            y >= logoBounds.top && y <= logoBounds.bottom) {
          return true;
        }
      }
      return false;
    }
    
    function handleTextDragStart(e) {
      if (!el.textPreview) return;
      
      const coords = getCanvasCoords(e, el.textPreview);
      const hitText = hitTestText(coords.x, coords.y);
      const hitLogo = hitTestLogo(coords.x, coords.y);
      
      if (hitText) {
        e.preventDefault();
        state.dragging.active = true;
        state.dragging.textLine = hitText;
        state.dragging.isLogo = false;
        state.dragging.startX = coords.x;
        state.dragging.startY = coords.y;
        
        // Calculate offset from click point to text center
        const textData = state.textLines[hitText];
        const textCenterX = ENGRAVE_BOUNDARY.left + (ENGRAVE_BOUNDARY.width * textData.x);
        const textCenterY = ENGRAVE_BOUNDARY.top + (ENGRAVE_BOUNDARY.height * textData.y);
        state.dragging.offsetX = coords.x - textCenterX;
        state.dragging.offsetY = coords.y - textCenterY;
        
        el.textPreview.style.cursor = 'grabbing';
        renderTextPreview();
      } else if (hitLogo) {
        e.preventDefault();
        state.dragging.active = true;
        state.dragging.textLine = null;
        state.dragging.isLogo = true;
        state.dragging.startX = coords.x;
        state.dragging.startY = coords.y;
        
        // Calculate offset from click point to logo center
        const logoCenterX = ENGRAVE_BOUNDARY.left + (ENGRAVE_BOUNDARY.width * state.imageTransform.x);
        const logoCenterY = ENGRAVE_BOUNDARY.top + (ENGRAVE_BOUNDARY.height * state.imageTransform.y);
        state.dragging.offsetX = coords.x - logoCenterX;
        state.dragging.offsetY = coords.y - logoCenterY;
        
        el.textPreview.style.cursor = 'grabbing';
        renderTextPreview();
      }
    }
    
    function handleTextDragMove(e) {
      if (!el.textPreview) return;
      
      const coords = getCanvasCoords(e, el.textPreview);
      
      if (state.dragging.active && state.dragging.textLine) {
        e.preventDefault();
        
        // Subtract offset so text doesn't jump to cursor position
        const adjustedX = coords.x - state.dragging.offsetX;
        const adjustedY = coords.y - state.dragging.offsetY;
        
        // Calculate new position as percentage of boundary size
        // The render function will apply hard-stop constraints based on actual text dimensions
        const relX = (adjustedX - ENGRAVE_BOUNDARY.left) / ENGRAVE_BOUNDARY.width;
        const relY = (adjustedY - ENGRAVE_BOUNDARY.top) / ENGRAVE_BOUNDARY.height;
        
        // Allow full range - render function will clamp based on text size
        const newX = Math.max(0, Math.min(1, relX));
        const newY = Math.max(0, Math.min(1, relY));
        
        state.textLines[state.dragging.textLine].x = newX;
        state.textLines[state.dragging.textLine].y = newY;
        
        renderTextPreview();
      } else if (state.dragging.active && state.dragging.isLogo) {
        e.preventDefault();
        
        // Subtract offset so logo doesn't jump to cursor position
        const adjustedX = coords.x - state.dragging.offsetX;
        const adjustedY = coords.y - state.dragging.offsetY;
        
        // Calculate new position as percentage of boundary size
        // The render function will apply hard-stop constraints based on actual logo dimensions
        const relX = (adjustedX - ENGRAVE_BOUNDARY.left) / ENGRAVE_BOUNDARY.width;
        const relY = (adjustedY - ENGRAVE_BOUNDARY.top) / ENGRAVE_BOUNDARY.height;
        
        // Allow full range - render function will clamp based on logo size
        const newX = Math.max(0, Math.min(1, relX));
        const newY = Math.max(0, Math.min(1, relY));
        
        state.imageTransform.x = newX;
        state.imageTransform.y = newY;
        
        renderTextPreview();
      } else {
        // Update cursor based on hover state
        const hitText = hitTestText(coords.x, coords.y);
        const hitLogo = hitTestLogo(coords.x, coords.y);
        el.textPreview.style.cursor = (hitText || hitLogo) ? 'grab' : 'default';
      }
    }
    
    function handleTextDragEnd(e) {
      if (state.dragging.active) {
        state.dragging.active = false;
        state.dragging.textLine = null;
        state.dragging.isLogo = false;
        state.finalSvgString = null; // Clear cached SVG when position changes
        
        if (el.textPreview) {
          const coords = getCanvasCoords(e, el.textPreview);
          const hitText = hitTestText(coords.x, coords.y);
          const hitLogo = hitTestLogo(coords.x, coords.y);
          el.textPreview.style.cursor = (hitText || hitLogo) ? 'grab' : 'default';
        }
        
        renderTextPreview();
        resetSvgPreview();
      }
    }

    // ========== Event Handlers ==========
    function setupEventListeners() {
      // Guide toggle
      el.guideToggle?.addEventListener('click', () => {
        if (el.stepperGuide) {
          el.stepperGuide.classList.toggle('collapsed');
          const toggleText = el.guideToggle.querySelector('.guide-toggle-text');
          if (toggleText) {
            toggleText.textContent = el.stepperGuide.classList.contains('collapsed') ? 'Show Guide' : 'Hide Guide';
          }
        }
      });

      // Navigation (all back/next button instances)
      el.btnsBack.forEach(btn => {
        btn.addEventListener('click', () => {
          if (state.currentStep > 1) {
            // In text-only mode, skip Step 2 (go from Step 3 directly to Step 1)
            if (state.textOnlyMode && state.currentStep === 3) {
              state.textOnlyMode = false; // Exit text-only mode
              state.completedSteps.delete(1);
              state.completedSteps.delete(2);
              updateTextOnlyUI();
              goToStep(1);
            } else {
              goToStep(state.currentStep - 1);
            }
          }
        });
      });

      el.btnsNext.forEach(btn => {
        btn.addEventListener('click', () => {
          if (state.currentStep < 5 && canProceed()) {
            state.completedSteps.add(state.currentStep);
            goToStep(state.currentStep + 1);
          }
        });
      });

      // Step 1: Upload
      el.uploadZone?.addEventListener('click', (e) => {
        // Don't trigger if clicking on the label, input, or button (they handle it naturally)
        if (e.target.tagName === 'LABEL' || e.target.tagName === 'INPUT' || e.target.tagName === 'BUTTON') return;
        // Don't trigger if clicking inside a label or button
        if (e.target.closest('label') || e.target.closest('button')) return;
        el.uploadInput?.click();
      });
      
      el.uploadZone?.addEventListener('dragover', (e) => {
        e.preventDefault();
        el.uploadZone.classList.add('dragover');
      });

      el.uploadZone?.addEventListener('dragleave', () => {
        el.uploadZone.classList.remove('dragover');
      });

      el.uploadZone?.addEventListener('drop', (e) => {
        e.preventDefault();
        el.uploadZone.classList.remove('dragover');
        const file = e.dataTransfer.files[0];
        if (file) handleFileUpload(file);
      });

      el.uploadInput?.addEventListener('change', () => {
        const file = el.uploadInput.files[0];
        if (file) handleFileUpload(file);
      });

      el.changeImageBtn?.addEventListener('click', () => {
        state.originalImage = null;
        state.processedImageData = null;
        state.logoContentBounds = null;
        state.logoAutoSized = false; // Reset auto-sizing for new image
        state.logoFitScale = 1.0; // Reset fit scale
        state.fullResLogoSvg = null; // Reset cached full-res SVG
        state.fullResLogoDimensions = null;
        state.detectedColors = [];
        state.colorModeEnabled = false;
        state.colorModeSlidersReset = false; // Reset so sliders will reset on next color mode entry
        state.finalSvgString = null; // Clear cached SVG when image changes
        state.textOnlyMode = false; // Exit text-only mode when changing image
        // Reset image transform to center
        state.imageTransform.x = 0.5;
        state.imageTransform.y = 0.5;
        state.imageTransform.scale = 1.0;
        if (el.colorModeCheckbox) el.colorModeCheckbox.checked = false;
        if (el.colorPaletteContainer) el.colorPaletteContainer.style.display = 'none';
        // Reset file input so the same file can be re-selected
        if (el.uploadInput) el.uploadInput.value = '';
        // Show logo controls again
        updateTextOnlyUI();
        // Immediately open file picker for convenience
        el.uploadInput?.click();
      });

      // Text Only button - skip image upload and go directly to text
      // Shared handler for both upload zone and footer buttons
      function handleTextOnlyClick(e) {
        e.preventDefault();
        e.stopPropagation();
        
        state.textOnlyMode = true;
        state.originalImage = null;
        state.processedImageData = null;
        state.logoContentBounds = null;
        state.logoAutoSized = false; // Reset auto-sizing
        state.logoFitScale = 1.0; // Reset fit scale
        state.fullResLogoSvg = null; // Reset cached full-res SVG
        state.fullResLogoDimensions = null;
        state.finalSvgString = null;
        
        // Mark steps 1 and 2 as completed
        state.completedSteps.add(1);
        state.completedSteps.add(2);
        
        // Update UI for text-only mode
        updateTextOnlyUI();
        
        // Jump to step 3
        goToStep(3);
      }
      
      el.textOnlyBtn?.addEventListener('click', handleTextOnlyClick);
      el.textOnlyFooterBtn?.addEventListener('click', handleTextOnlyClick);

      // Color selection mode
      el.colorModeCheckbox?.addEventListener('change', () => {
        state.colorModeEnabled = el.colorModeCheckbox.checked;
        state.colorModeSlidersReset = false; // Reset so sliders will reset when entering Step 2
        state.finalSvgString = null; // Clear cached SVG when color mode changes
        el.colorPaletteContainer.style.display = state.colorModeEnabled ? '' : 'none';
        
        if (state.colorModeEnabled && state.originalImage) {
          if (state.detectedColors.length === 0) {
            analyzeAndRenderColors();
          } else {
            processImageWithColors();
          }
        } else if (!state.colorModeEnabled && state.originalImage) {
          // Reset sliders to standard defaults when disabling color mode
          resetSlidersToStandardDefaults();
          // Revert to simple threshold mode
          processImage(state.originalImage, ctx.converted, el.convertedPreview);
        }
      });

      el.colorCountSelect?.addEventListener('change', () => {
        state.colorCount = parseInt(el.colorCountSelect.value);
        // Auto re-analyze when color count changes
        if (state.originalImage && state.colorModeEnabled) {
          analyzeAndRenderColors();
        }
      });

      el.selectAllColorsBtn?.addEventListener('click', () => {
        state.detectedColors.forEach(c => c.selected = true);
        state.finalSvgString = null; // Clear cached SVG when color selection changes
        renderColorPalette();
        processImageWithColors();
        resetSvgPreview();
      });

      el.deselectAllColorsBtn?.addEventListener('click', () => {
        state.detectedColors.forEach(c => c.selected = false);
        state.finalSvgString = null; // Clear cached SVG when color selection changes
        renderColorPalette();
        processImageWithColors();
        resetSvgPreview();
      });

      el.invertColorsBtn?.addEventListener('click', () => {
        state.detectedColors.forEach(c => c.selected = !c.selected);
        state.finalSvgString = null; // Clear cached SVG when color selection changes
        renderColorPalette();
        processImageWithColors();
        resetSvgPreview();
      });

      // Step 2: Adjust
      el.thresholdSlider?.addEventListener('input', () => {
        state.threshold = parseInt(el.thresholdSlider.value);
        state.finalSvgString = null; // Clear cached SVG when settings change
        el.thresholdValue.textContent = state.threshold;
        renderAdjustPreview();
        resetSvgPreview();
      });

      el.contrastSlider?.addEventListener('input', () => {
        state.contrast = parseFloat(el.contrastSlider.value);
        state.finalSvgString = null; // Clear cached SVG when settings change
        el.contrastValue.textContent = state.contrast.toFixed(1);
        renderAdjustPreview();
        resetSvgPreview();
      });

      // --- Fill Gaps toggle ---
      el.fillGapsOn?.addEventListener('click', () => {
        state.fillGaps = true;
        state.finalSvgString = null;
        el.fillGapsOn.classList.add('active');
        el.fillGapsOff.classList.remove('active');
        if (el.gapFillSizeGroup) el.gapFillSizeGroup.style.display = '';
        renderAdjustPreview();
        resetSvgPreview();
      });

      el.fillGapsOff?.addEventListener('click', () => {
        state.fillGaps = false;
        state.finalSvgString = null;
        el.fillGapsOff.classList.add('active');
        el.fillGapsOn.classList.remove('active');
        if (el.gapFillSizeGroup) el.gapFillSizeGroup.style.display = 'none';
        renderAdjustPreview();
        resetSvgPreview();
      });

      // --- Gap Fill Size slider ---
      el.gapFillSizeSlider?.addEventListener('input', () => {
        state.gapFillSize = parseInt(el.gapFillSizeSlider.value);
        state.finalSvgString = null;
        if (el.gapFillSizeValue) el.gapFillSizeValue.textContent = state.gapFillSize;
        renderAdjustPreview();
        resetSvgPreview();
      });

      // --- Preprocessing controls ---
      el.blurSlider?.addEventListener('input', () => {
        state.blurRadius = parseFloat(el.blurSlider.value);
        state.finalSvgString = null;
        if (el.blurValue) el.blurValue.textContent = state.blurRadius.toFixed(1);
        renderAdjustPreview();
        resetSvgPreview();
      });

      el.brightnessSlider?.addEventListener('input', () => {
        state.brightness = parseInt(el.brightnessSlider.value);
        state.finalSvgString = null;
        if (el.brightnessValue) el.brightnessValue.textContent = state.brightness;
        renderAdjustPreview();
        resetSvgPreview();
      });

      el.despeckleSlider?.addEventListener('input', () => {
        state.despeckleSize = parseInt(el.despeckleSlider.value);
        state.finalSvgString = null;
        if (el.despeckleValue) el.despeckleValue.textContent = state.despeckleSize;
        renderAdjustPreview();
        resetSvgPreview();
      });

      // Anti-aliasing presets (Off / Smart / High)
      function setAAPreset(mode) {
        state.finalSvgString = null;
        state.antiAliasing = mode !== 'off';
        el.antiAliasingOff?.classList.toggle('active', mode === 'off');
        el.antiAliasingOn?.classList.toggle('active', mode === 'smart');
        el.antiAliasingHigh?.classList.toggle('active', mode === 'high');
        renderAdjustPreview();
        resetSvgPreview();
      }
      el.antiAliasingOff?.addEventListener('click', () => setAAPreset('off'));
      el.antiAliasingOn?.addEventListener('click', () => setAAPreset('smart'));
      el.antiAliasingHigh?.addEventListener('click', () => setAAPreset('high'));

      // Noise cleanup presets (Off / Low / High)
      function setNoisePreset(mode) {
        state.finalSvgString = null;
        if (mode === 'off') {
          state.despeckleSize = 0;
          state.turdsize = 0;
        } else if (mode === 'low') {
          state.despeckleSize = 20;
          state.turdsize = 4;
        } else {
          state.despeckleSize = 80;
          state.turdsize = 10;
        }
        // Sync advanced sliders
        if (el.despeckleSlider) el.despeckleSlider.value = state.despeckleSize;
        if (el.despeckleValue) el.despeckleValue.textContent = state.despeckleSize;
        if (el.turdsizeSlider) el.turdsizeSlider.value = state.turdsize;
        if (el.turdsizeValue) el.turdsizeValue.textContent = state.turdsize;
        el.noiseOff?.classList.toggle('active', mode === 'off');
        el.noiseLow?.classList.toggle('active', mode === 'low');
        el.noiseHigh?.classList.toggle('active', mode === 'high');
        renderAdjustPreview();
        resetSvgPreview();
      }
      el.noiseOff?.addEventListener('click', () => setNoisePreset('off'));
      el.noiseLow?.addEventListener('click', () => setNoisePreset('low'));
      el.noiseHigh?.addEventListener('click', () => setNoisePreset('high'));

      // Detail level presets (Fine / Balanced / Simple)
      function setDetailPreset(mode) {
        state.finalSvgString = null;
        if (mode === 'fine') {
          state.cornerSharpness = 0.5;
          state.curveOptimization = 0.1;
        } else if (mode === 'balanced') {
          state.cornerSharpness = 1.0;
          state.curveOptimization = 0.2;
        } else {
          state.cornerSharpness = 1.34;
          state.curveOptimization = 0.5;
        }
        // Sync advanced sliders
        if (el.cornerSlider) el.cornerSlider.value = state.cornerSharpness;
        if (el.cornerValue) el.cornerValue.textContent = state.cornerSharpness.toFixed(2);
        el.detailFine?.classList.toggle('active', mode === 'fine');
        el.detailBalanced?.classList.toggle('active', mode === 'balanced');
        el.detailSimple?.classList.toggle('active', mode === 'simple');
        renderAdjustPreview();
        resetSvgPreview();
      }
      el.detailFine?.addEventListener('click', () => setDetailPreset('fine'));
      el.detailBalanced?.addEventListener('click', () => setDetailPreset('balanced'));
      el.detailSimple?.addEventListener('click', () => setDetailPreset('simple'));

      // Adaptive threshold checkbox
      el.adaptiveThreshold?.addEventListener('change', () => {
        state.thresholdMode = el.adaptiveThreshold.checked ? 'adaptive' : 'global';
        state.finalSvgString = null;
        // Dim the threshold slider when adaptive is active
        if (el.thresholdSlider) {
          el.thresholdSlider.disabled = el.adaptiveThreshold.checked;
          el.thresholdSlider.style.opacity = el.adaptiveThreshold.checked ? '0.4' : '1';
        }
        renderAdjustPreview();
        resetSvgPreview();
      });

      // --- Vectorization controls ---
      el.turdsizeSlider?.addEventListener('input', () => {
        state.turdsize = parseInt(el.turdsizeSlider.value);
        state.finalSvgString = null;
        if (el.turdsizeValue) el.turdsizeValue.textContent = state.turdsize;
        renderAdjustPreview();
        resetSvgPreview();
      });

      el.cornerSlider?.addEventListener('input', () => {
        state.cornerSharpness = parseFloat(el.cornerSlider.value);
        state.finalSvgString = null;
        if (el.cornerValue) el.cornerValue.textContent = state.cornerSharpness.toFixed(2);
        renderAdjustPreview();
        resetSvgPreview();
      });

      el.optToleranceSlider?.addEventListener('input', () => {
        state.curveOptimization = parseFloat(el.optToleranceSlider.value);
        state.finalSvgString = null;
        if (el.optToleranceValue) el.optToleranceValue.textContent = state.curveOptimization.toFixed(1);
        renderAdjustPreview();
        resetSvgPreview();
      });

      el.turnPolicySelect?.addEventListener('change', () => {
        state.turnPolicy = el.turnPolicySelect.value;
        state.finalSvgString = null;
        renderAdjustPreview();
        resetSvgPreview();
      });

      el.minDetailSlider?.addEventListener('input', () => {
        state.minDetailSize = parseInt(el.minDetailSlider.value);
        state.finalSvgString = null;
        if (el.minDetailValue) el.minDetailValue.textContent = state.minDetailSize;
        renderAdjustPreview();
        resetSvgPreview();
      });

      el.invertBtn?.addEventListener('click', () => {
        state.inverted = !state.inverted;
        state.finalSvgString = null; // Clear cached SVG when settings change
        el.invertBtn.textContent = state.inverted ? 'Invert Colors (ON)' : 'Invert Colors';
        renderAdjustPreview();
        resetSvgPreview();
      });

      // Step 3: Text
      setupTextListeners('top');
      setupTextListeners('middle');
      setupTextListeners('bottom');

      // Text preview canvas drag events
      if (el.textPreview) {
        el.textPreview.addEventListener('mousedown', handleTextDragStart);
        el.textPreview.addEventListener('mousemove', handleTextDragMove);
        el.textPreview.addEventListener('mouseup', handleTextDragEnd);
        el.textPreview.addEventListener('mouseleave', handleTextDragEnd);
        
        // Touch support for mobile
        el.textPreview.addEventListener('touchstart', handleTextDragStart, { passive: false });
        el.textPreview.addEventListener('touchmove', handleTextDragMove, { passive: false });
        el.textPreview.addEventListener('touchend', handleTextDragEnd);
        el.textPreview.addEventListener('touchcancel', handleTextDragEnd);
      }

      el.resetPositions?.addEventListener('click', () => {
        // Reset all text positions to defaults
        state.textLines.top.x = 0.2;
        state.textLines.top.y = 0.82;
        state.textLines.middle.x = 0.2;
        state.textLines.middle.y = 0.88;
        state.textLines.bottom.x = 0.2;
        state.textLines.bottom.y = 0.94;
        
        // Reset logo transform to defaults
        state.imageTransform.x = 0.5;
        state.imageTransform.y = 0.4;
        
        // Reset scale to 30% of fit scale (same as initial auto-size)
        const resetSliderValue = 30;
        state.imageTransform.scale = state.logoFitScale * (resetSliderValue / 100);
        
        // Update scale slider (normalized range: 5-100)
        if (el.logoScaleSlider) {
          el.logoScaleSlider.min = 5;
          el.logoScaleSlider.max = 100;
          el.logoScaleSlider.step = 1;
          el.logoScaleSlider.value = resetSliderValue;
        }
        if (el.logoScaleValue) el.logoScaleValue.textContent = `${resetSliderValue}%`;
        
        state.finalSvgString = null; // Clear cached SVG when positions reset
        renderTextPreview();
        resetSvgPreview();
      });
      
      // Logo scale slider (value is % of fit scale: 5-100)
      el.logoScaleSlider?.addEventListener('input', () => {
        // Slider value represents percentage of fit scale (5% to 100%)
        const sliderValue = parseInt(el.logoScaleSlider.value, 10);
        
        // Calculate actual scale from slider value
        state.imageTransform.scale = state.logoFitScale * (sliderValue / 100);
        
        if (el.logoScaleValue) el.logoScaleValue.textContent = `${sliderValue}%`;
        state.finalSvgString = null;
        renderTextPreview();
        resetSvgPreview();
      });

      // Add text line button - shows next hidden text line
      el.addTextLineBtn?.addEventListener('click', () => {
        const linesData = [
          { el: el.textLine2, position: 'middle' },
          { el: el.textLine3, position: 'bottom' }
        ];
        for (const lineData of linesData) {
          if (lineData.el && lineData.el.classList.contains('text-line-hidden')) {
            lineData.el.classList.remove('text-line-hidden');
            // Enable this text line in state
            if (state.textLines[lineData.position]) {
              state.textLines[lineData.position].enabled = true;
            }
            break;
          }
        }
        // Hide button if all lines are visible
        updateAddTextLineButton();
        updateTextSummary();
      });

      // Remove text line buttons (X buttons)
      root.querySelectorAll('.remove-text-line-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const targetId = btn.getAttribute('data-target');
          const lineEl = root.querySelector(`#${targetId}`);
          if (lineEl) {
            // Hide the line and remove has-text class
            lineEl.classList.add('text-line-hidden');
            lineEl.classList.remove('has-text');
            
            // Clear the input and update state
            const input = lineEl.querySelector('.text-input');
            if (input) {
              input.value = '';
              const position = input.getAttribute('data-position');
              if (position && state.textLines[position]) {
                state.textLines[position].text = '';
                state.textLines[position].enabled = false;
              }
            }
            
            // Show the Add button again
            updateAddTextLineButton();
            
            // Update preview and summary
            state.finalSvgString = null;
            updateTextSummary();
            renderTextPreview();
            resetSvgPreview();
          }
        });
      });

      el.confirmStep3?.addEventListener('click', () => {
        state.completedSteps.add(3);
        goToStep(4);
      });

      // Step 4: Product Preview
      // Step 5: Purchase - quantity controls
      root.querySelectorAll('.qty-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const action = btn.dataset.action;
          const product = btn.dataset.product;
          
          if (action === 'increase') {
            state.quantities[product] += 2;
          } else if (action === 'decrease' && state.quantities[product] >= 2) {
            state.quantities[product] -= 2;
          }
          
          updateOrderSummary();
        });
      });

      el.addToCartBtn?.addEventListener('click', addToCart);
      el.downloadPng?.addEventListener('click', downloadPng);
      el.downloadSvg?.addEventListener('click', downloadSvg);
      el.downloadBoth?.addEventListener('click', downloadBoth);
      
      // Steps 1-3 use canvas preview only (no SVG toggle)
      // Steps 4 and 5 are SVG-only, no toggle needed
      
      // Text format buttons (B/I/U)
      root.querySelectorAll('.text-format-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const format = btn.getAttribute('data-format');
          const position = btn.getAttribute('data-position');
          if (format && position && state.textLines[position]) {
            state.textLines[position][format] = !state.textLines[position][format];
            btn.classList.toggle('active', state.textLines[position][format]);
            state.finalSvgString = null;
            renderTextPreview();
            resetSvgPreview();
          }
        });
      });
      
      // Text size selectors
      root.querySelectorAll('.text-size-select').forEach(select => {
        select.addEventListener('change', () => {
          const position = select.getAttribute('data-position');
          if (position && state.textLines[position]) {
            state.textLines[position].size = select.value;
            state.finalSvgString = null;
            renderTextPreview();
            resetSvgPreview();
          }
        });
      });
    }

    function setupTextListeners(position) {
      // Query elements directly (more reliable than pre-stored references)
      const inputEl = root.querySelector(`#text-${position}-input-${sectionId}`);
      const fontEl = root.querySelector(`#text-${position}-font-${sectionId}`);

      // Text line 1 (top) is always enabled since it's always visible
      // Lines 2 and 3 are enabled when added via "Add Another" button
      if (position === 'top') {
        state.textLines[position].enabled = true;
      }

      if (inputEl) {
        // Max character limit (based on 40mm width at typical font size)
        const MAX_CHARS = 30;
        inputEl.setAttribute('maxlength', MAX_CHARS);
        
        // Add character counter if not already present
        let charCounter = inputEl.parentElement?.querySelector('.char-counter');
        if (!charCounter) {
          charCounter = document.createElement('span');
          charCounter.className = 'char-counter';
          charCounter.style.cssText = 'font-size: 11px; color: #888; margin-left: 8px;';
          inputEl.parentElement?.appendChild(charCounter);
        }
        
        // Handler for text input changes
        const handleTextInput = () => {
          // Enforce character limit
          if (inputEl.value.length > MAX_CHARS) {
            inputEl.value = inputEl.value.slice(0, MAX_CHARS);
          }
          
          state.textLines[position].text = inputEl.value;
          state.finalSvgString = null; // Clear cached SVG when text content changes
          
          // Update character counter
          if (charCounter) {
            const remaining = MAX_CHARS - inputEl.value.length;
            charCounter.textContent = `${remaining} chars left`;
            charCounter.style.color = remaining < 5 ? '#cc0000' : '#888';
          }
          
          // Toggle has-text class for visual feedback
          const lineGroup = inputEl.closest('.text-line-group');
          if (lineGroup) {
            lineGroup.classList.toggle('has-text', inputEl.value.trim().length > 0);
          }
          
          updateTextSummary();
          renderTextPreview();
          resetSvgPreview();
        };
        
        // Initialize character counter
        handleTextInput();
        
        // Listen to multiple events to ensure live updates
        inputEl.addEventListener('input', handleTextInput);
        inputEl.addEventListener('keyup', handleTextInput);
        inputEl.addEventListener('change', handleTextInput);
      }

      if (fontEl) {
        fontEl.addEventListener('change', () => {
          state.textLines[position].font = fontEl.value;
          state.finalSvgString = null; // Clear cached SVG when font changes
          renderTextPreview();
          resetSvgPreview();
        });
      }
    }

    function updateTextSummary() {
      const count = countTextLines();
      // These elements may not exist if text price summary was removed
      if (el.textLineCount) el.textLineCount.textContent = count;
      if (el.textTotal) el.textTotal.textContent = `$${count * config.textLineFee}`;
    }

    function updateAddTextLineButton() {
      const lines = [el.textLine2, el.textLine3];
      const hiddenLines = lines.filter(l => l && l.classList.contains('text-line-hidden'));
      if (el.addTextLineBtn) {
        if (hiddenLines.length === 0) {
          el.addTextLineBtn.classList.add('hidden');
        } else {
          el.addTextLineBtn.classList.remove('hidden');
        }
      }
    }

    function capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    function handleFileUpload(file) {
      if (!file.type.startsWith('image/')) {
        el.processingStatus.textContent = 'Please upload an image file';
        return;
      }

      state.originalFileName = file.name.replace(/\.[^.]+$/, '');
      el.processingStatus.textContent = 'Processing image...';

      const url = URL.createObjectURL(file);
      const img = new Image();

      img.onload = () => {
        state.originalImage = img;
        state.finalSvgString = null; // Clear cached SVG when new image is loaded
        state.logoAutoSized = false; // Reset auto-sizing for new image
        state.logoFitScale = 1.0; // Reset fit scale for new image
        state.fullResLogoSvg = null; // Reset cached full-res SVG
        state.fullResLogoDimensions = null;
        state.upscaledCanvas = null; // Reset upscaled cache for new image
        state.upscaleInfo = null;
        state.previewSvg = null;
        
        // Show original
        el.originalPreview.src = url;
        
        // Process and show converted
        processImage(img, ctx.converted, el.convertedPreview);
        
        el.uploadZone.style.display = 'none';
        el.uploadPreview.style.display = '';
        el.processingStatus.textContent = '';
        
        updateUI();
        resetSvgPreview(); // Show vector preview after processing image
      };

      img.onerror = () => {
        el.processingStatus.textContent = 'Failed to load image';
        URL.revokeObjectURL(url);
      };

      img.src = url;
    }

    // ========== Initialize ==========
    initHighDpiCanvases();
    setupEventListeners();
    updateUI();
  }
})();
